import { useTeacherUnsavedChanges, useTeacherWorkspace } from '../../../components/layout/TeacherWorkspace';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Tab } from '@headlessui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { lessonPlansApi } from '../../../services/apiV2';
import { LessonResource, LessonStep, SimulationTemplateMetadata } from '../../../types/v2';
import { BlockEditor, StepBlock } from '../../../components/v2/BlockEditor';
import { StepPreviewModal } from '../../../components/v2/StepPreviewModal';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { Button } from '../../../components/ui/button';
import { MatchingDraftRow, matchingRowsToPairs, normalizeMatchingDraftRows } from '../../../utils/matchingPairs';
import { normalizeStepPacingTimer } from '../../../utils/pacingTimer';
import { LessonMaterialsEditor, lessonMaterialIssue } from '../../../components/v2/LessonMaterialsEditor';
import { SimpleActivityEditor } from '../../../components/v2/teacher/SimpleActivityEditor';
import { activityPresets, activityPresetPatch, ActivityPreset, activityLabel } from '../../../utils/activityAuthoring';

type EditorStep = Omit<LessonStep, 'id'> & { id: number | string };
type AssessmentDeliverySource = {
  assessment_id: number;
  assessment_title: string;
  version_number: number;
  published_at: string;
};
type UrgencyPresetKey = 'warm_up' | 'think_write_share' | 'paced_chunk' | 'productive_struggle';
type ImportMode = 'append' | 'update';
type ImportPreviewExistingRow = { order: number; title: string; stageTag: string };
type ImportPreviewIncomingRow = {
  action: 'create' | 'update';
  sourceIndex: number;
  requestedOrder: number;
  finalOrder: number;
  targetStepId?: number | null;
  title: string;
  stageTag: string;
  stepKind: string;
};
type ImportPreviewState = {
  mode: ImportMode;
  source: 'file' | 'text';
  existingCount: number;
  incomingCount: number;
  createdCount: number;
  updatedCount: number;
  finalCount: number;
  existingRows: ImportPreviewExistingRow[];
  incomingRows: ImportPreviewIncomingRow[];
  warnings: string[];
};

const stageOptions = [
  { value: 'why', label: 'WHY' },
  { value: 'define', label: 'DEFINE' },
  { value: 'do', label: 'DO' },
  { value: 'explain', label: 'EXPLAIN' },
  { value: 'apply', label: 'APPLY' },
  { value: 'reflect', label: 'REFLECT' },
];

const stepKindOptions = ['bell_ringer', 'instruction', 'practice', 'assessment', 'reflection', 'exit_ticket', 'discussion'];
const runnerPhaseValues: Array<NonNullable<LessonStep['runner_phase']>> = ['live', 'studio'];
const runnerPhaseOptions = [
  { value: 'live', label: 'Live' },
  { value: 'studio', label: 'Independent Work' },
];
const liveResponseModeValues: Array<NonNullable<LessonStep['live_response_mode']>> = [
  'none',
  'quick_check',
  'guided_response',
  'practice_checkpoint',
];
const liveResponseModeOptions = [
  { value: 'none', label: 'Select live response' },
  { value: 'quick_check', label: 'Quick check' },
  { value: 'guided_response', label: 'Guided response' },
  { value: 'practice_checkpoint', label: 'Practice checkpoint' },
];
const artifactRequirementValues: Array<NonNullable<LessonStep['artifact_requirement']>> = [
  'none',
  'apply_required',
];
const artifactLocationValues: Array<NonNullable<LessonStep['artifact_location']>> = [
  'none',
  'headrush',
  'elevate_internal',
];
const artifactLocationOptions = [
  { value: 'none', label: 'Select location' },
  { value: 'headrush', label: 'Headrush' },
  { value: 'elevate_internal', label: 'Elevate Internal' },
];
const coachModeValues: Array<NonNullable<LessonStep['coach_mode']>> = [
  'off',
  'optional',
];
const structuredSignalTypes = ['pairs', 'matching', 'ordering', 'drag_drop', 'drag_drop_code', 'drag_drop_fill_blanks', 'drag_drop_keywords'];
const textSignalTypes = ['text', 'short_answer', 'open_response', 'free_response', 'paragraph', 'reflection', 'written_response'];
const DISPLAY_INPUT_SIMULATION_TEMPLATE_ID = 'display_input_no_signal_v1';

const urgencyPresetLabels: Record<UrgencyPresetKey, string> = {
  warm_up: '2-minute Warm Up',
  think_write_share: 'Think → Write → Discuss → Share',
  paced_chunk: '5–7 minute lesson chunks',
  productive_struggle: 'Productive-struggle practice',
};

const normalizeSignalType = (value: any): string => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/-/g, '_');

const normalizeEnumValue = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T => {
  const normalized = String(value || '').trim().toLowerCase();
  return (allowed as readonly string[]).includes(normalized) ? (normalized as T) : fallback;
};

type GuidedSequenceValidationNumberKey = 'min_words' | 'min_chars' | 'max_chars';

const optionalPositiveIntegerDisplayValue = (value: unknown): string => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : '';
};

const updateGuidedSequenceCardValidationNumber = (
  currentSignalConfig: Record<string, any>,
  cardIndex: number,
  key: GuidedSequenceValidationNumberKey,
  rawValue: string,
): Record<string, any> => {
  const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
  const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
    ? { ...currentCards[cardIndex] }
    : {};
  const validation = (currentCard.validation && typeof currentCard.validation === 'object')
    ? { ...currentCard.validation }
    : {};
  const trimmed = rawValue.trim();
  const nextValue = Number(trimmed);
  if (trimmed && Number.isInteger(nextValue) && nextValue > 0) {
    validation[key] = nextValue;
  } else {
    delete validation[key];
  }
  if (Object.keys(validation).length > 0) {
    currentCard.validation = validation;
  } else {
    delete currentCard.validation;
  }
  currentCards[cardIndex] = currentCard;
  return { ...currentSignalConfig, cards: currentCards };
};

const updateGuidedSequenceCardValidationText = (
  currentSignalConfig: Record<string, any>,
  cardIndex: number,
  key: 'must_start_with',
  rawValue: string,
): Record<string, any> => {
  const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
  const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
    ? { ...currentCards[cardIndex] }
    : {};
  const validation = (currentCard.validation && typeof currentCard.validation === 'object')
    ? { ...currentCard.validation }
    : {};
  const nextValue = rawValue.trim();
  if (nextValue) {
    validation[key] = nextValue;
  } else {
    delete validation[key];
  }
  if (Object.keys(validation).length > 0) {
    currentCard.validation = validation;
  } else {
    delete currentCard.validation;
  }
  currentCards[cardIndex] = currentCard;
  return { ...currentSignalConfig, cards: currentCards };
};

const getDefaultLiveResponseMode = (step: Partial<EditorStep>): 'quick_check' | 'guided_response' | 'practice_checkpoint' => {
  const stepKind = String(step.step_kind || '').trim().toLowerCase();
  const stageTag = String(step.stage_tag || '').trim().toLowerCase();
  const signalType = normalizeSignalType(step.signal_config?.type || step.signal_config?.signal_type);
  if (stepKind === 'practice' || stageTag === 'do') return 'practice_checkpoint';
  if (textSignalTypes.includes(signalType) || stageTag === 'explain' || stageTag === 'reflect') return 'guided_response';
  return 'quick_check';
};

const normalizeEditorStep = (step: LessonStep): EditorStep => {
  const nextStep = { ...step } as EditorStep;
  const contentActivityType = String(step.content?.activity_type || '').trim().toLowerCase();
  const signalActivityType = normalizeSignalType(step.signal_config?.type || step.signal_config?.signal_type);
  const isSimulation = step.activity_format === 'simulation'
    || contentActivityType === 'simulation'
    || signalActivityType === 'simulation_activity';
  nextStep.activity_format = isSimulation ? 'simulation' : 'standard';
  nextStep.simulation_template_id = step.simulation_template_id
    || (typeof step.content?.simulation?.case_id === 'string' ? step.content.simulation.case_id : null);
  nextStep.simulation_objective = step.simulation_objective
    || (typeof step.content?.simulation?.presentation?.objective === 'string'
      ? step.content.simulation.presentation.objective
      : null);
  nextStep.runner_phase = normalizeEnumValue(step.runner_phase, runnerPhaseValues, 'live');
  nextStep.live_response_mode = normalizeEnumValue(step.live_response_mode, liveResponseModeValues, 'none');
  nextStep.artifact_requirement = normalizeEnumValue(step.artifact_requirement, artifactRequirementValues, 'none');
  nextStep.artifact_location = normalizeEnumValue(step.artifact_location, artifactLocationValues, 'none');
  nextStep.artifact_config = (step.artifact_config && typeof step.artifact_config === 'object')
    ? { ...step.artifact_config }
    : {};
  nextStep.coach_mode = normalizeEnumValue(step.coach_mode, coachModeValues, 'off');
  nextStep.content = (step.content && typeof step.content === 'object') ? { ...step.content } : {};
  nextStep.evidence_config = (step.evidence_config && typeof step.evidence_config === 'object') ? { ...step.evidence_config } : {};
  nextStep.signal_config = (step.signal_config && typeof step.signal_config === 'object') ? { ...step.signal_config } : {};

  if (
    nextStep.activity_format !== 'simulation'
    && nextStep.live_response_mode === 'none'
    && Object.keys(nextStep.signal_config).length > 0
  ) {
    nextStep.live_response_mode = getDefaultLiveResponseMode(nextStep);
  }

  return nextStep;
};

const getArtifactConfig = (step: Partial<EditorStep>): Record<string, any> => (
  step.artifact_config && typeof step.artifact_config === 'object' ? step.artifact_config : {}
);

const getArtifactPrompt = (step: Partial<EditorStep>): string => {
  const artifactConfig = getArtifactConfig(step);
  return typeof artifactConfig.prompt === 'string' ? artifactConfig.prompt : '';
};

const getArtifactExternalUrl = (step: Partial<EditorStep>): string => {
  const artifactConfig = getArtifactConfig(step);
  return typeof artifactConfig.external_url === 'string' ? artifactConfig.external_url : '';
};

const getArtifactExternalAssignmentId = (step: Partial<EditorStep>): string => {
  const artifactConfig = getArtifactConfig(step);
  if (typeof artifactConfig.external_assignment_id === 'string') return artifactConfig.external_assignment_id;
  if (typeof artifactConfig.external_assignment_id === 'number') return String(artifactConfig.external_assignment_id);
  return '';
};

const buildDefaultGuidedSequenceCards = () => ([
  {
    key: 'root_cause',
    prompt: 'Exact Root Cause (one sentence)',
    input_type: 'text',
    required: true,
    placeholder: 'When ___ happens, ___ fails because ___.',
    validation: { min_words: 6, max_chars: 220 },
  },
  {
    key: 'interruption_mechanism',
    prompt: 'Interruption Mechanism (what physically changes?)',
    input_type: 'text',
    required: true,
    placeholder: 'What will you add/change/remove in the real world?',
    validation: { min_words: 4, max_chars: 220 },
  },
  {
    key: 'activation_moment',
    prompt: 'Activation Moment (when exactly does it happen?)',
    input_type: 'text',
    required: true,
    placeholder: 'At the moment when ___...',
    validation: { min_words: 4, max_chars: 220 },
  },
  {
    key: 'trade_off',
    prompt: 'Trade-Off (what becomes harder or slower?)',
    input_type: 'text',
    required: true,
    placeholder: 'This makes ___ slower/harder because ___.',
    validation: { min_words: 4, max_chars: 220 },
  },
  {
    key: 'measurement_plan',
    prompt: 'Measurement Plan (how will you know it works?)',
    input_type: 'text',
    required: true,
    placeholder: 'We will measure success by ___ over ___ days/weeks.',
    validation: { min_words: 6, max_chars: 260 },
  },
  {
    key: 'system_statement',
    prompt: 'Now write the final system statement exactly using your answers.',
    input_type: 'textarea',
    required: true,
    template: 'My system reduces ______ by interrupting ______ at the moment when ______. The trade-off is ______. We will measure success by ______.',
    validation: { min_words: 12, max_chars: 340, must_start_with: 'My system reduces' },
  },
]);

const lessonReportStageOrder = ['why', 'define', 'do', 'explain', 'apply', 'reflect'] as const;
const lessonReportStageHeaders: Record<string, string> = {
  why: 'WHY',
  define: 'DEFINE',
  do: 'DO',
  explain: 'EXPLAIN',
  apply: 'APPLY',
  reflect: 'REFLECT',
};

const asRecord = (value: any): Record<string, any> => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const readString = (value: any): string => (typeof value === 'string' ? value : '');

const readFirstString = (obj: Record<string, any>, keys: string[]): string => {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

type StepSupportConfig = {
  enabled: boolean;
  student_model_md: string;
  teacher_explanation_md: string;
  hint_md: string;
  common_confusion_md: string;
  vocabulary: string[];
};

const normalizeStepSupport = (value: any): StepSupportConfig => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const vocabulary: string[] = Array.isArray(source.vocabulary)
    ? source.vocabulary
      .map((item: any) => String(item || '').trim())
      .filter(Boolean)
    : [];
  const normalizedVocabulary: string[] = Array.from(new Set(vocabulary.map((item: string) => item.toLowerCase())));
  return {
    enabled: source.enabled !== false,
    student_model_md: readString(source.student_model_md),
    teacher_explanation_md: readString(source.teacher_explanation_md),
    hint_md: readString(source.hint_md),
    common_confusion_md: readString(source.common_confusion_md),
    vocabulary: normalizedVocabulary
      .map((tokenLower: string) => vocabulary.find((item: string) => item.toLowerCase() === tokenLower) || tokenLower),
  };
};

const readFirstValue = (obj: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    if (obj[key] !== undefined) return obj[key];
  }
  return undefined;
};

const normalizeStageTag = (step: any): string => {
  const raw = readFirstString(asRecord(step), ['stage_tag', 'stageTag']).toLowerCase();
  return raw;
};

const normalizeStepOrder = (step: any): number => {
  const raw = Number(readFirstValue(asRecord(step), ['order']));
  return Number.isFinite(raw) ? raw : Number.MAX_SAFE_INTEGER;
};

const importTargetStepIdKeys = ['id', 'step_id', 'target_step_id', 'existing_step_id'];

const getImportTargetStepId = (stepRaw: any): number | null => {
  if (!stepRaw || typeof stepRaw !== 'object' || Array.isArray(stepRaw)) return null;
  for (const key of importTargetStepIdKeys) {
    const rawValue = stepRaw[key];
    if (rawValue === undefined || rawValue === null || rawValue === '') continue;
    const parsed = Number(rawValue);
    if (Number.isInteger(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const collectApiErrorMessages = (value: any): string[] => {
  if (!value) return [];
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap((item) => collectApiErrorMessages(item));
  if (typeof value === 'object') {
    return Object.values(value).flatMap((item) => collectApiErrorMessages(item));
  }
  return [];
};

const extractApiErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const messages = collectApiErrorMessages(data)
    .map((message) => String(message || '').trim())
    .filter(Boolean);
  return messages[0] || err?.message || fallback;
};

const formatLocalIso = (date: Date = new Date()): string => {
  const pad = (value: number) => String(Math.trunc(Math.abs(value))).padStart(2, '0');
  const tzMinutes = -date.getTimezoneOffset();
  const sign = tzMinutes >= 0 ? '+' : '-';
  const tzHours = Math.floor(Math.abs(tzMinutes) / 60);
  const tzRemainder = Math.abs(tzMinutes) % 60;
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(tzHours)}:${pad(tzRemainder)}`
  );
};

const formatLessonPlanMarkdown = (
  plan: { id: number | string; title: string; hookId: string; hookTitle?: string },
  steps: EditorStep[],
): string => {
  const sortedSteps = [...steps].sort((a, b) => {
    const orderA = normalizeStepOrder(a);
    const orderB = normalizeStepOrder(b);
    if (orderA !== orderB) return orderA - orderB;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });

  const hookId = readString(plan.hookId).trim();
  const hookTitle = readString(plan.hookTitle || '').trim();
  const hookLabel = hookTitle && hookId ? `${hookTitle} (ID: ${hookId})` : (hookTitle || hookId || 'Unknown');
  const lines: string[] = [
    `# Lesson Plan: ${readString(plan.title).trim() || 'Untitled Plan'}`,
    `Hook: ${hookLabel}`,
    `Plan ID: ${String(plan.id || '').trim() || 'Unknown'}`,
    `Generated At: ${formatLocalIso()}`,
    '',
  ];

  const renderStep = (step: EditorStep, index: number) => {
    const stepAny = asRecord(step);
    const content = asRecord(readFirstValue(stepAny, ['content']));
    const directSignalConfig = asRecord(readFirstValue(stepAny, ['signal_config', 'signalConfig']));
    const suggestedSignalConfig = asRecord(readFirstValue(content, ['suggested_signal_config', 'suggestedSignalConfig']));
    const signalConfig = Object.keys(directSignalConfig).length > 0 ? directSignalConfig : suggestedSignalConfig;
    const evidenceConfig = asRecord(readFirstValue(stepAny, ['evidence_config', 'evidenceConfig']));
    const rawOrder = Number(readFirstValue(stepAny, ['order']));
    const stepOrderDisplay = Number.isFinite(rawOrder) ? rawOrder : 0;
    const stepTitle = readString(stepAny.title).trim() || 'Untitled step';
    const stepKind = readFirstString(stepAny, ['step_kind', 'stepKind']) || 'instruction';
    const promptMd = readFirstString(content, ['prompt_md', 'promptMd']);
    const expectedProficiency = readFirstString(content, ['expected_proficiency', 'expectedProficiency']);
    const expectedMastery = readFirstString(content, ['expected_mastery', 'expectedMastery']);
    const countsAsEvidenceValue = readFirstValue(stepAny, ['counts_as_evidence', 'countsAsEvidence']);
    const runnerPhase = readFirstString(stepAny, ['runner_phase', 'runnerPhase']) || 'live';
    const liveResponseMode = readFirstString(stepAny, ['live_response_mode', 'liveResponseMode']) || 'none';
    const artifactRequirement = readFirstString(stepAny, ['artifact_requirement', 'artifactRequirement']) || 'none';
    const artifactLocation = readFirstString(stepAny, ['artifact_location', 'artifactLocation']) || 'none';
    const artifactConfig = asRecord(readFirstValue(stepAny, ['artifact_config', 'artifactConfig']));
    const coachMode = readFirstString(stepAny, ['coach_mode', 'coachMode']) || 'off';
    const hasSignal = Object.keys(signalConfig).length > 0;
    const hasEvidenceConfig = Object.keys(evidenceConfig).length > 0;
    const hasEvidence = countsAsEvidenceValue !== undefined || hasEvidenceConfig;
    const hasArtifact = artifactRequirement !== 'none' || Object.keys(artifactConfig).length > 0;
    const blocks = Array.isArray(content.blocks) ? content.blocks : [];
    const hasBlocks = blocks.length > 0;
    const teacherNotes = readString(content.teacher_notes).trim();
    const stageTag = normalizeStageTag(step);
    const stageLabel = lessonReportStageHeaders[stageTag] || (stageTag ? stageTag.toUpperCase() : 'UNSPECIFIED');

    lines.push(`### Step ${index} — ${stepTitle} (${stepKind})`);
    lines.push(`Step ID: ${String(stepAny.id ?? '').trim() || 'unknown'}`);
    lines.push(`Plan Order: ${stepOrderDisplay}`);
    lines.push(`Stage: ${stageLabel}`);
    lines.push(`Runner Phase: ${runnerPhase === 'studio' ? 'Independent Work' : 'Live'}`);
    lines.push('');

    if (promptMd) {
      lines.push('Prompt:');
      lines.push(promptMd);
      lines.push('');
    }

    if (hasBlocks) {
      lines.push('Student Content Blocks:');
      blocks.forEach((rawBlock: any, idx: number) => {
        const block = asRecord(rawBlock);
        const blockType = readFirstString(block, ['type']).toLowerCase() || 'text';
        if (blockType === 'text') {
          const text = readString(block.text).trim();
          if (text) {
            lines.push(`- [Text ${idx + 1}]`);
            lines.push(text);
          }
          return;
        }
        if (blockType === 'code') {
          const language = readString(block.language).trim();
          const code = readString(block.code);
          lines.push(`- [Code ${idx + 1}]${language ? ` (${language})` : ''}`);
          lines.push(`\`\`\`${language || ''}`.trim());
          lines.push(code || '');
          lines.push('```');
          return;
        }
        if (blockType === 'link') {
          const label = readString(block.label).trim();
          const url = readString(block.url).trim();
          lines.push(`- [Link ${idx + 1}] ${label || '(no label)'}${url ? ` -> ${url}` : ''}`);
          return;
        }
        lines.push(`- [${blockType || 'block'} ${idx + 1}]`);
        lines.push('```json');
        lines.push(JSON.stringify(block, null, 2));
        lines.push('```');
      });
      lines.push('');
    }

    if (teacherNotes) {
      lines.push('Teacher Notes:');
      lines.push(teacherNotes);
      lines.push('');
    }

    if (hasSignal) {
      lines.push('Signal:');
      const signalType = normalizeSignalType(readFirstString(signalConfig, ['type', 'signal_type', 'signalType']));
      const signalPrompt = readFirstString(signalConfig, ['prompt']);
      const revealMode = readFirstString(signalConfig, ['reveal_mode', 'revealMode']);
      const reasonRequired = readFirstValue(signalConfig, ['reason_required', 'reasonRequired']);
      const confidenceRequired = readFirstValue(signalConfig, ['confidence_required', 'confidenceRequired']);
      if (signalType) lines.push(`- type: ${signalType}`);
      if (signalPrompt) lines.push(`- prompt: ${signalPrompt}`);
      if (typeof reasonRequired === 'boolean') lines.push(`- reason_required: ${reasonRequired}`);
      if (typeof confidenceRequired === 'boolean') lines.push(`- confidence_required: ${confidenceRequired}`);
      if (revealMode) lines.push(`- reveal_mode: ${revealMode}`);

      const replyHint = readFirstString(signalConfig, ['reply_format', 'replyFormat', 'format', 'signal_type', 'signalType']);
      if (replyHint) {
        lines.push('Reply format hint (optional):');
        lines.push(`- ${replyHint}`);
      }
      if (Array.isArray(signalConfig.options) && signalConfig.options.length > 0) {
        lines.push('- options:');
        signalConfig.options.forEach((option: any, idx: number) => {
          lines.push(`  - ${idx}: ${String(option || '')}`);
        });
      }
      if (Array.isArray(signalConfig.cards) && signalConfig.cards.length > 0) {
        lines.push(`- cards: ${signalConfig.cards.length}`);
        signalConfig.cards.forEach((card: any, idx: number) => {
          const c = asRecord(card);
          const key = readString(c.key).trim() || `card_${idx + 1}`;
          const cardPrompt = readString(c.prompt).trim();
          const inputType = readString(c.input_type).trim();
          lines.push(`  - ${idx + 1}. ${key}${inputType ? ` (${inputType})` : ''}${cardPrompt ? `: ${cardPrompt}` : ''}`);
        });
      }
      lines.push('- signal_config:');
      lines.push('```json');
      lines.push(JSON.stringify(signalConfig, null, 2));
      lines.push('```');
      lines.push('');
    }

    if (runnerPhase !== 'live' || liveResponseMode !== 'none' || hasArtifact) {
      lines.push('Runtime Boundary:');
      lines.push(`- runner_phase: ${runnerPhase}`);
      lines.push(`- live_response_mode: ${liveResponseMode}`);
      lines.push(`- artifact_requirement: ${artifactRequirement}`);
      lines.push(`- artifact_location: ${artifactLocation}`);
      lines.push(`- coach_mode: ${coachMode}`);
      if (hasArtifact) {
        lines.push('- artifact_config:');
        lines.push('```json');
        lines.push(JSON.stringify(artifactConfig, null, 2));
        lines.push('```');
      }
      lines.push('');
    }

    if (hasEvidence) {
      lines.push('Evidence:');
      if (typeof countsAsEvidenceValue === 'boolean') {
        lines.push(`- counts_as_evidence: ${countsAsEvidenceValue}`);
      } else if (countsAsEvidenceValue !== undefined) {
        lines.push(`- counts_as_evidence: ${String(countsAsEvidenceValue)}`);
      }
      if (hasEvidenceConfig) {
        lines.push('- evidence_config:');
        lines.push('```json');
        lines.push(JSON.stringify(evidenceConfig, null, 2));
        lines.push('```');
      }
      lines.push('');
    }

    if (expectedProficiency) {
      lines.push('Expected Proficiency:');
      lines.push(expectedProficiency);
      lines.push('');
    }

    if (expectedMastery) {
      lines.push('Expected Mastery:');
      lines.push(expectedMastery);
      lines.push('');
    }
  };

  lines.push('## Steps (Plan Order)');
  if (sortedSteps.length === 0) {
    lines.push('_No steps in this plan._');
    lines.push('');
  } else {
    sortedSteps.forEach((step, idx) => renderStep(step, idx + 1));
  }

  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()}\n`;
};

const normalizeBlocks = (content: any): StepBlock[] => {
  if (Array.isArray(content?.blocks)) return content.blocks;
  if (typeof content === 'string') return [{ type: 'text', text: content }];
  if (typeof content?.text === 'string') return [{ type: 'text', text: content.text }];
  return [];
};

const getTeacherNotes = (content: any) => (typeof content?.teacher_notes === 'string' ? content.teacher_notes : '');

const getQuizConfig = (content: any) => ({
  source: content?.quiz_config?.source || '',
  topic: content?.quiz_config?.topic || '',
  question_count: content?.quiz_config?.question_count || 5,
});

const buildContent = (content: any, blocks: StepBlock[], teacherNotes: string, quizConfig: any, isQuiz: boolean) => {
  const base = content && typeof content === 'object' ? content : {};
  const nextContent: Record<string, any> = {
    ...base,
    blocks,
    teacher_notes: teacherNotes,
  };
  if (isQuiz) {
    nextContent.quiz_config = quizConfig;
  }
  return nextContent;
};

const requiresSignalPrompt = (type: any) => {
  const t = normalizeSignalType(type);
  return t === 'text' || t === 'mcq';
};

const normalizeSignalConfigForSave = (step: EditorStep) => {
  const signalConfig = (step.signal_config && typeof step.signal_config === 'object')
    ? { ...step.signal_config }
    : {};
  const type = normalizeSignalType(signalConfig.type);
  const prompt = typeof signalConfig.prompt === 'string' ? signalConfig.prompt.trim() : '';
  if ((type === 'text' || type === 'mcq') && !prompt) {
    const promptFromContent = typeof step.content?.prompt_md === 'string' ? step.content.prompt_md.trim() : '';
    const promptFromTitle = typeof step.title === 'string' ? step.title.trim() : '';
    const fallbackPrompt = promptFromContent || promptFromTitle;
    if (fallbackPrompt) {
      signalConfig.prompt = fallbackPrompt;
    }
  }
  if (type === 'guided_sequence' && !Array.isArray(signalConfig.cards)) {
    signalConfig.cards = buildDefaultGuidedSequenceCards();
  }
  return signalConfig;
};

const renderTokenForMarkdown = (token: string) => {
  const value = String(token || '').trim();
  if (!value) return '';
  if (/^[+\-*]$/.test(value) || value === '**') return `\`${value}\``;
  return value.replace(/^([+\-*])/, '\\$1');
};

const buildMatchingPromptMd = (question: string, pairs: any) => {
  const lines = normalizeMatchingDraftRows(pairs)
    .map((row) => [String(row.left || '').trim(), String(row.right || '').trim()])
    .filter(([left, right]) => left && right)
    .map(([left, right]) => `${renderTokenForMarkdown(left)} -> ${renderTokenForMarkdown(right)}`);
  const q = String(question || '').trim();
  return [q, lines.length ? `Pairs:\n${lines.join('\n')}` : ''].filter(Boolean).join('\n\n');
};

const buildOrderingPromptMd = (question: string, items: string[], answer: string[]) => {
  const q = String(question || '').trim();
  const cleanItems = (Array.isArray(items) ? items : [])
    .map((item) => renderTokenForMarkdown(String(item || '').trim()))
    .filter(Boolean);
  const cleanAnswer = (Array.isArray(answer) ? answer : [])
    .map((item) => renderTokenForMarkdown(String(item || '').trim()))
    .filter(Boolean);
  return [
    q,
    cleanItems.length ? `Items:\n${cleanItems.join('\n')}` : '',
    cleanAnswer.length ? `Correct order:\n${cleanAnswer.map((item, idx) => `${idx + 1}) ${item}`).join('\n')}` : '',
  ].filter(Boolean).join('\n\n');
};

const buildDragDropCodePromptMd = (question: string, target: string, tokens: string[]) => {
  const q = String(question || '').trim();
  const t = String(target || '').trim();
  const cleanTokens = (Array.isArray(tokens) ? tokens : [])
    .map((item) => renderTokenForMarkdown(String(item || '').trim()))
    .filter(Boolean);
  return [
    q,
    t ? `Template:\n${t}` : '',
    cleanTokens.length ? `Token bank:\n${cleanTokens.join(' ')}` : '',
  ].filter(Boolean).join('\n\n');
};

const normalizeStructuredPromptContent = (content: any) => {
  const nextContent = content && typeof content === 'object' ? { ...content } : {};
  // Draft rows preserve incomplete/duplicate keys while editing, never publish them.
  delete nextContent.authoring_pairs;
  const sourceItem = (
    nextContent.source_item && typeof nextContent.source_item === 'object'
  ) ? nextContent.source_item : (
    nextContent.sourceItem && typeof nextContent.sourceItem === 'object' ? nextContent.sourceItem : null
  );
  const sourceType = String(sourceItem?.type || '').trim().toLowerCase();
  if (sourceType === 'matching') {
    const pairs = sourceItem?.pairs && typeof sourceItem.pairs === 'object' ? sourceItem.pairs : {};
    nextContent.source_item = {
      ...(sourceItem || {}),
      pairs: matchingRowsToPairs(normalizeMatchingDraftRows(pairs)),
    };
    delete nextContent.sourceItem;
    nextContent.prompt_md = buildMatchingPromptMd(String(sourceItem?.question || ''), pairs);
  } else if (sourceType === 'ordering') {
    const items = Array.isArray(sourceItem?.items) ? sourceItem.items : [];
    const answer = Array.isArray(sourceItem?.answer) ? sourceItem.answer : [];
    nextContent.prompt_md = buildOrderingPromptMd(String(sourceItem?.question || ''), items, answer);
  } else if (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks') {
    const tokens = Array.isArray(sourceItem?.tokens)
      ? sourceItem.tokens
      : (Array.isArray(sourceItem?.options) ? sourceItem.options : []);
    const target = String(sourceItem?.target || sourceItem?.code || sourceItem?.template || '').trim();
    const configuredBlanksRaw = Number(sourceItem?.blanks);
    const configuredBlanks = Number.isInteger(configuredBlanksRaw) && configuredBlanksRaw > 0 ? configuredBlanksRaw : 0;
    const answerTokensCount = Array.isArray(sourceItem?.answer_tokens) ? sourceItem.answer_tokens.length : 0;
    const targetBlankCount = (target.match(/____/g) || []).length;
    const tokenCount = tokens.length;
    const detectedBlankCount = configuredBlanks > 0
      ? configuredBlanks
      : (answerTokensCount > 0 ? answerTokensCount : (targetBlankCount > 0 ? targetBlankCount : tokenCount));
    nextContent.source_item = {
      ...(sourceItem || {}),
      blanks: detectedBlankCount,
    };
    delete nextContent.sourceItem;
    nextContent.prompt_md = buildDragDropCodePromptMd(String(sourceItem?.question || ''), target, tokens);
  }
  return nextContent;
};

export const V2LessonPlanEditor: React.FC = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_lesson_plan_editor');
  const [advancedEditor, setAdvancedEditor] = useState(false);
  const [activityPickerOpen, setActivityPickerOpen] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const isDev = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const [searchParams] = useSearchParams();
  const legacyEvidenceCompatibilityMode = searchParams.get('legacyEvidence') === '1'
    || searchParams.get('migrationCompatibility') === '1';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contentFormat, setContentFormat] = useState<'lesson' | 'activity'>('lesson');
  const [hookLevel, setHookLevel] = useState('');
  const [weekNumber, setWeekNumber] = useState('');
  const [hookId, setHookId] = useState('');
  const [hookTitle, setHookTitle] = useState('');
  const [defaultPacing, setDefaultPacing] = useState('whole_class');
  const [isTemplate, setIsTemplate] = useState(false);
  const [resources, setResources] = useState<LessonResource[]>([]);
  const [assessmentSource, setAssessmentSource] = useState<AssessmentDeliverySource | null>(null);
  const [steps, setSteps] = useState<EditorStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<number | string | null>(null);
  const [dirtySteps, setDirtySteps] = useState<Set<number | string>>(new Set());
  const [orderDirty, setOrderDirty] = useState(false);
  const [planDirty, setPlanDirty] = useState(false);
  const [isDeletingStep, setIsDeletingStep] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [saveError, setSaveError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [copyMarkdownStatus, setCopyMarkdownStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importText, setImportText] = useState('');
  const [importMode, setImportMode] = useState<ImportMode>('append');
  const [importLoading, setImportLoading] = useState(false);
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importPreview, setImportPreview] = useState<ImportPreviewState | null>(null);
  const [importPreviewBundle, setImportPreviewBundle] = useState<any | null>(null);
  const [whyEvidenceNotice, setWhyEvidenceNotice] = useState('');
  const [matchingDraftRows, setMatchingDraftRows] = useState<Record<string, MatchingDraftRow[]>>({});
  const [sourceItemJsonDraft, setSourceItemJsonDraft] = useState<Record<string, string>>({});
  const [sourceItemJsonErrors, setSourceItemJsonErrors] = useState<Record<string, string>>({});
  const [simulationTemplates, setSimulationTemplates] = useState<SimulationTemplateMetadata[]>([]);
  const [simulationTemplatesError, setSimulationTemplatesError] = useState('');

  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || Number(a.id) - Number(b.id)),
    [steps]
  );
  const selectedStep = steps.find((step) => step.id === selectedStepId) || null;
  const selectedStepIsSimulation = selectedStep?.activity_format === 'simulation';
  const selectedStepHasLiveResponse = Boolean(
    selectedStep && !selectedStepIsSimulation && (
      (selectedStep.live_response_mode && selectedStep.live_response_mode !== 'none')
      || Object.keys(selectedStep.signal_config || {}).length > 0
    )
  );
  const selectedStepResponseLabel = selectedStep?.runner_phase === 'studio' ? 'Independent Work Response' : 'Live Response';
  const selectedStepCollectResponseLabel = selectedStep?.runner_phase === 'studio' ? 'Collect Independent Work response' : 'Collect live response';
  const selectedStepResponseHelpText = selectedStep?.runner_phase === 'studio'
    ? 'This controls the response type students see during Independent Work.'
    : 'This controls the in-run response type students see during the lesson.';
  const selectedStepRequiresOfficialArtifact = selectedStep?.artifact_requirement === 'apply_required';
  const selectedStepArtifactPrompt = selectedStep ? getArtifactPrompt(selectedStep) : '';
  const selectedStepArtifactExternalUrl = selectedStep ? getArtifactExternalUrl(selectedStep) : '';
  const selectedStepArtifactExternalAssignmentId = selectedStep ? getArtifactExternalAssignmentId(selectedStep) : '';
  const selectedStepSupport = selectedStep ? normalizeStepSupport((selectedStep.content as Record<string, any>)?.support) : normalizeStepSupport({});
  const selectedStepPacingTimer = selectedStep ? normalizeStepPacingTimer((selectedStep.content as Record<string, any>) || {}) : normalizeStepPacingTimer({});
  const showLegacyEvidencePanel = Boolean(
    selectedStep
    && !selectedStepIsSimulation
    && (legacyEvidenceCompatibilityMode || selectedStep.counts_as_evidence)
  );
  const previousAnswersSourceCandidates = useMemo(() => {
    if (!selectedStep) return [] as Array<{ id: number; title: string; order: number }>;
    const selectedOrderRaw = Number(selectedStep.order);
    const selectedOrder = Number.isFinite(selectedOrderRaw) ? selectedOrderRaw : Number.MAX_SAFE_INTEGER;
    return sortedSteps
      .filter((step) => typeof step.id === 'number' && step.id !== selectedStep.id)
      .filter((step) => {
        const stepOrderRaw = Number(step.order);
        const stepOrder = Number.isFinite(stepOrderRaw) ? stepOrderRaw : Number.MAX_SAFE_INTEGER;
        return stepOrder < selectedOrder;
      })
      .map((step) => ({
        id: Number(step.id),
        title: String(step.title || '').trim() || `Step ${step.order}`,
        order: Number(step.order),
      }));
  }, [selectedStep, sortedSteps]);

  useEffect(() => {
    const hookParam = searchParams.get('hookId');
    if (hookParam) {
      setHookId(hookParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadPlan = async () => {
      if (!planId) return;
      const planRes = await lessonPlansApi.get(Number(planId));
      const plan = planRes.data;
      setTitle(plan.title || '');
      setDescription(plan.description || '');
      setContentFormat(plan.content_format || 'lesson');
      setHookLevel(plan.hook_level || '');
      setWeekNumber(plan.week_number ? String(plan.week_number) : '');
      setHookId(String(plan.hook || ''));
      setHookTitle(String(plan.hook_title || plan.hookTitle || plan.hook_name || plan.hookName || '').trim());
      setDefaultPacing(plan.default_pacing || 'whole_class');
      setIsTemplate(Boolean(plan.is_template));
      setResources(Array.isArray(plan.resources) ? plan.resources : []);
      setAssessmentSource(plan.assessment_source || null);

      const stepsRes = await lessonPlansApi.steps(Number(planId));
      const stepArr = Array.isArray(stepsRes.data) ? stepsRes.data : [];
      const nextDirtySteps = new Set<number | string>();
      const nextSteps = stepArr.map((step: LessonStep) => {
        const nextStep = normalizeEditorStep(step);
        const signalConfig = { ...nextStep.signal_config };
        if (Object.keys(signalConfig).length > 0 && requiresSignalPrompt(signalConfig.type) && !String(signalConfig.prompt || '').trim()) {
          const fallbackPrompt = String(nextStep.content?.prompt_md || nextStep.title || '').trim();
          if (fallbackPrompt) {
            signalConfig.prompt = fallbackPrompt;
            nextStep.signal_config = signalConfig;
            nextDirtySteps.add(nextStep.id);
          }
        }
        return nextStep;
      });
      setSteps(nextSteps);
      setDirtySteps((prev) => new Set([...Array.from(prev), ...Array.from(nextDirtySteps)]));
      setSelectedStepId(nextSteps[0]?.id || null);
      try {
        const templateRes = await lessonPlansApi.simulationTemplates(Number(planId));
        const templates = Array.isArray(templateRes?.data?.templates) ? templateRes.data.templates : [];
        setSimulationTemplates(templates);
        setSimulationTemplatesError('');
      } catch {
        setSimulationTemplates([]);
        setSimulationTemplatesError('Simulation templates are unavailable. Standard step editing is still available.');
      }
      if (isDev) {
        const blockSummary = nextSteps
          .map((step) => ({
            id: step.id,
            blocks: Array.isArray(step.content?.blocks) ? step.content.blocks.length : 0,
            hasPromptMd: Boolean(typeof step.content?.prompt_md === 'string' && step.content.prompt_md.trim()),
          }))
          .filter((item) => item.blocks > 0 || item.hasPromptMd);
        console.debug('[LessonPlanEditor] loaded step content summary', blockSummary);
      }
    };
    loadPlan();
  }, [planId, isDev]);

  const refreshStepsFromServer = useCallback(async () => {
    if (!planId) return;
    const stepsRes = await lessonPlansApi.steps(Number(planId));
    const stepArr = Array.isArray(stepsRes.data) ? stepsRes.data : [];
    const nextDirtySteps = new Set<number | string>();
    const nextSteps = stepArr.map((step: LessonStep) => {
      const nextStep = normalizeEditorStep(step);
      const signalConfig = { ...nextStep.signal_config };
      if (Object.keys(signalConfig).length > 0 && requiresSignalPrompt(signalConfig.type) && !String(signalConfig.prompt || '').trim()) {
        const fallbackPrompt = String(nextStep.content?.prompt_md || nextStep.title || '').trim();
        if (fallbackPrompt) {
          signalConfig.prompt = fallbackPrompt;
          nextStep.signal_config = signalConfig;
          nextDirtySteps.add(nextStep.id);
        }
      }
      return nextStep;
    });
    setSteps(nextSteps);
    setDirtySteps(nextDirtySteps);
    setOrderDirty(false);
    setSelectedStepId((prev) => (prev && nextSteps.some((step) => step.id === prev) ? prev : (nextSteps[0]?.id || null)));
    if (isDev) {
      const blockSummary = nextSteps
        .map((step) => ({
          id: step.id,
          blocks: Array.isArray(step.content?.blocks) ? step.content.blocks.length : 0,
          hasPromptMd: Boolean(typeof step.content?.prompt_md === 'string' && step.content.prompt_md.trim()),
        }))
        .filter((item) => item.blocks > 0 || item.hasPromptMd);
      console.debug('[LessonPlanEditor] refreshed step content summary', blockSummary);
    }
  }, [planId, isDev]);

  const markStepDirty = (id: number | string) => {
    setDirtySteps((prev) => new Set(prev).add(id));
  };

  const updateStep = (id: number | string, patch: Partial<EditorStep>) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== id) return step;
        const nextStep = { ...step, ...patch };
        if (nextStep.stage_tag === 'why' && nextStep.counts_as_evidence) {
          nextStep.counts_as_evidence = false;
          setWhyEvidenceNotice('Evidence disabled on WHY steps.');
          window.setTimeout(() => setWhyEvidenceNotice(''), 2000);
        }
        if (nextStep.stage_tag !== 'apply' && nextStep.artifact_requirement === 'apply_required') {
          nextStep.artifact_requirement = 'none';
          nextStep.artifact_location = 'none';
          nextStep.artifact_config = {};
          nextStep.coach_mode = 'off';
          setWhyEvidenceNotice('Official artifacts only stay enabled on APPLY steps.');
          window.setTimeout(() => setWhyEvidenceNotice(''), 2000);
        }
        if (Object.keys(nextStep.signal_config || {}).length === 0) {
          nextStep.live_response_mode = 'none';
        } else if (!nextStep.live_response_mode || nextStep.live_response_mode === 'none') {
          nextStep.live_response_mode = getDefaultLiveResponseMode(nextStep);
        }
        return nextStep;
      })
    );
    markStepDirty(id);
  };

  const updateActivityFormat = (step: EditorStep, activityFormat: 'standard' | 'simulation') => {
    const pacingTimer = normalizeStepPacingTimer(step.content);
    if (activityFormat === 'simulation') {
      const template = simulationTemplates.find(
        (item) => item.id === DISPLAY_INPUT_SIMULATION_TEMPLATE_ID,
      );
      if (!template) {
        setSaveError('Simulation templates are unavailable. Try again after refreshing.');
        return;
      }
      updateStep(step.id, {
        activity_format: 'simulation',
        simulation_template_id: template.id,
        simulation_objective: template.default_objective,
        title: !String(step.title || '').trim() || step.title === 'New Step'
          ? template.default_title
          : step.title,
        step_kind: 'practice',
        counts_as_evidence: false,
        live_response_mode: 'none',
        artifact_requirement: 'none',
        artifact_location: 'none',
        artifact_config: {},
        coach_mode: 'off',
        content: { pacing_timer: pacingTimer },
        evidence_config: {},
        signal_config: {},
      });
      setSaveError('');
      return;
    }
    updateStep(step.id, {
      activity_format: 'standard',
      simulation_template_id: null,
      simulation_objective: null,
      counts_as_evidence: false,
      live_response_mode: 'none',
      artifact_requirement: 'none',
      artifact_location: 'none',
      artifact_config: {},
      coach_mode: 'off',
      content: {
        blocks: [],
        pacing_timer: pacingTimer,
      },
      evidence_config: {},
      signal_config: {},
    });
  };

  const updateStepSignalConfig = (
    id: number | string,
    updater: (currentSignalConfig: Record<string, any>) => Record<string, any>
  ) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== id) return step;
        const currentSignalConfig = (step.signal_config && typeof step.signal_config === 'object')
          ? step.signal_config
          : {};
        const nextSignalConfig = updater(currentSignalConfig);
        const nextStep = {
          ...step,
          signal_config: nextSignalConfig,
        };
        if (nextStep.stage_tag === 'why' && nextStep.counts_as_evidence) {
          nextStep.counts_as_evidence = false;
          setWhyEvidenceNotice('Evidence disabled on WHY steps.');
          window.setTimeout(() => setWhyEvidenceNotice(''), 2000);
        }
        if (Object.keys(nextSignalConfig || {}).length === 0) {
          nextStep.live_response_mode = 'none';
        } else if (!nextStep.live_response_mode || nextStep.live_response_mode === 'none') {
          nextStep.live_response_mode = getDefaultLiveResponseMode(nextStep);
        }
        return nextStep;
      })
    );
    markStepDirty(id);
  };

  const updateStepSupport = (
    id: number | string,
    updater: (currentSupport: StepSupportConfig) => StepSupportConfig,
  ) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== id) return step;
        const currentContent = (step.content && typeof step.content === 'object')
          ? { ...step.content }
          : {};
        const nextSupport = normalizeStepSupport(updater(normalizeStepSupport(currentContent.support)));
        return {
          ...step,
          content: {
            ...currentContent,
            support: nextSupport,
          },
        };
      }),
    );
    markStepDirty(id);
  };

  const updateStepPacingTimer = (
    id: number | string,
    updater: (currentTimer: ReturnType<typeof normalizeStepPacingTimer>) => ReturnType<typeof normalizeStepPacingTimer>,
  ) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== id) return step;
        const currentContent = (step.content && typeof step.content === 'object')
          ? { ...step.content }
          : {};
        const nextTimer = updater(normalizeStepPacingTimer(currentContent));
        return {
          ...step,
          content: {
            ...currentContent,
            pacing_timer: nextTimer,
          },
        };
      }),
    );
    markStepDirty(id);
  };

  const updateStepArtifactConfig = (
    id: number | string,
    updater: (currentArtifactConfig: Record<string, any>) => Record<string, any>,
  ) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id !== id) return step;
        const currentArtifactConfig = getArtifactConfig(step);
        const nextArtifactConfig = updater(currentArtifactConfig);
        return {
          ...step,
          artifact_config: nextArtifactConfig,
        };
      })
    );
    markStepDirty(id);
  };

  const addStep = (preset?: ActivityPreset) => {
    const nextOrder = steps.length > 0 ? Math.max(...steps.map((step) => step.order || 0)) + 1 : 1;
    const tempId = `temp-${Date.now()}`;
    const newStep: EditorStep = {
      id: tempId,
      lesson_plan: Number(planId),
      title: 'New Step',
      order: nextOrder,
      stage_tag: 'define',
      runner_phase: 'live',
      counts_as_evidence: false,
      live_response_mode: 'none',
      artifact_requirement: 'none',
      artifact_location: 'none',
      artifact_config: {},
      coach_mode: 'off',
      step_kind: 'instruction',
      content: { blocks: [] },
      evidence_config: {},
      signal_config: {},
      activity_format: 'standard',
      simulation_template_id: null,
      simulation_objective: null,
      ...(preset ? activityPresetPatch(preset) : {}),
    };
    if (preset === 'simulation') {
      const template = simulationTemplates[0];
      if (!template) return;
      newStep.activity_format = 'simulation';
      newStep.simulation_template_id = template.id;
      newStep.simulation_objective = template.default_objective;
    }
    setSteps((prev) => [...prev, newStep]);
    setSelectedStepId(tempId);
    markStepDirty(tempId);
    setActivityPickerOpen(false);
    if (preset) setAdvancedEditor(false);
  };

  const addUrgencyPreset = (preset: UrgencyPresetKey) => {
    const startOrder = steps.length > 0 ? Math.max(...steps.map((step) => step.order || 0)) + 1 : 1;
    const timestamp = Date.now();
    const makeStep = (
      index: number,
      title: string,
      prompt: string,
      durationSeconds: number,
      options?: {
        stage?: NonNullable<LessonStep['stage_tag']>;
        stepKind?: string;
        collectResponse?: boolean;
        scaffolds?: string[];
      },
    ): EditorStep => {
      const collectResponse = options?.collectResponse === true;
      return {
        id: `temp-urgency-${timestamp}-${index}`,
        lesson_plan: Number(planId),
        title,
        order: startOrder + index,
        stage_tag: options?.stage || 'define',
        runner_phase: 'live',
        counts_as_evidence: false,
        live_response_mode: collectResponse ? 'guided_response' : 'none',
        artifact_requirement: 'none',
        artifact_location: 'none',
        artifact_config: {},
        coach_mode: 'off',
        step_kind: options?.stepKind || (collectResponse ? 'practice' : 'instruction'),
        content: {
          blocks: [{ type: 'text', text: prompt }],
          prompt_md: prompt,
          pacing_timer: {
            enabled: true,
            duration_seconds: durationSeconds,
            label: title,
          },
          ...(options?.scaffolds ? { scaffold_prompts: options.scaffolds } : {}),
        },
        evidence_config: {},
        signal_config: collectResponse ? {
          type: 'text',
          prompt,
          reasoning: 'optional',
        } : {},
        activity_format: 'standard',
        simulation_template_id: null,
        simulation_objective: null,
      };
    };

    let presetSteps: EditorStep[] = [];
    if (preset === 'warm_up') {
      presetSteps = [makeStep(0, '2-Minute Warm Up', 'Respond to the posted warm-up prompt. Be ready to explain your thinking.', 120, {
        stage: 'why',
        stepKind: 'practice',
        collectResponse: true,
      })];
    } else if (preset === 'think_write_share') {
      presetSteps = [
        makeStep(0, 'Think', 'Read the discussion prompt and think silently. Identify the main idea you want to contribute.', 30),
        makeStep(1, 'Everybody Writes', 'Write your response before anyone shares. Include one reason or piece of evidence.', 120, {
          stage: 'explain',
          stepKind: 'practice',
          collectResponse: true,
        }),
        makeStep(2, 'Turn and Talk', 'Share your response with a partner. Listen for one idea that strengthens or challenges your thinking.', 120),
        makeStep(3, 'Share and Stretch', 'Be ready to share your response. Listen for how the follow-up question asks you to make the idea more precise.', 180),
      ];
    } else if (preset === 'paced_chunk') {
      presetSteps = [
        makeStep(0, 'Mini Input', 'Focus on the essential idea, model, or demonstration for this chunk.', 360, { stepKind: 'instruction' }),
        makeStep(1, 'Active Practice', 'Apply the idea to the short practice prompt. Submit your best current response.', 360, {
          stage: 'do',
          stepKind: 'practice',
          collectResponse: true,
        }),
        makeStep(2, 'Check for Understanding', 'Explain what worked, what remains unclear, or what evidence supports your response.', 180, {
          stage: 'explain',
          stepKind: 'practice',
          collectResponse: true,
        }),
      ];
    } else {
      presetSteps = [makeStep(0, 'Productive Struggle', 'Attempt the challenging task independently. Show the first step you can justify before requesting help.', 420, {
        stage: 'do',
        stepKind: 'practice',
        collectResponse: true,
        scaffolds: [
          'What is the first step you can complete with confidence?',
          'What do you already know that applies to this task?',
          'Try a different representation or approach. What changes?',
        ],
      })];
    }

    const tempIds = presetSteps.map((step) => step.id);
    setSteps((current) => [...current, ...presetSteps]);
    setDirtySteps((current) => {
      const next = new Set(current);
      tempIds.forEach((id) => next.add(id));
      return next;
    });
    setSelectedStepId(tempIds[0] || null);
  };

  const duplicateStep = () => {
    if (!selectedStep) return;
    const nextOrder = steps.length > 0 ? Math.max(...steps.map((step) => step.order || 0)) + 1 : 1;
    const tempId = `temp-${Date.now()}`;
    const clone: EditorStep = {
      ...selectedStep,
      id: tempId,
      order: nextOrder,
      title: `${selectedStep.title || 'Step'} (Copy)`,
    };
    setSteps((prev) => [...prev, clone]);
    setSelectedStepId(tempId);
    markStepDirty(tempId);
  };

  const deleteStep = async () => {
    if (!selectedStep) return false;
    const stepToDelete = selectedStep;
    if (typeof stepToDelete.id === 'number') {
      try {
        setSaveError('');
        setIsDeletingStep(true);
        await lessonPlansApi.deleteStep(stepToDelete.id);
      } catch {
        setSaveError('Failed to delete step. Refresh and try again.');
        return false;
      } finally {
        setIsDeletingStep(false);
      }
    }
    const remaining = steps.filter((step) => step.id !== stepToDelete.id);
    setSteps(remaining);
    setSelectedStepId(remaining[0]?.id || null);
    setOrderDirty(true);
    setDirtySteps((prev) => {
      const next = new Set(prev);
      next.delete(stepToDelete.id);
      return next;
    });
    const key = String(stepToDelete.id);
    setSourceItemJsonDraft((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSourceItemJsonErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setMatchingDraftRows((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    return true;
  };

  const moveStep = (id: number | string, direction: -1 | 1) => {
    const index = sortedSteps.findIndex((step) => step.id === id);
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= sortedSteps.length) return;
    const reordered = [...sortedSteps];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(nextIndex, 0, moved);
    const withOrder = reordered.map((step, idx) => ({ ...step, order: idx + 1 }));
    setSteps(withOrder);
    setOrderDirty(true);
    markStepDirty(id);
  };

  const workspace = useTeacherWorkspace();
  const returnToLibrary = () => { const go = () => navigate(hookId ? `/v2/teacher/plan/hooks/${hookId}` : '/v2/teacher/plan'); if (workspace) workspace.requestLeave(go); else go(); };
  const isDirty = planDirty || dirtySteps.size > 0 || orderDirty;
  useTeacherUnsavedChanges(isDirty);
  const evidenceBlocked = steps.some((step) => step.stage_tag === 'why' && step.counts_as_evidence);
  const simulationIssues = steps
    .filter((step) => step.activity_format === 'simulation')
    .map((step) => {
      const templateId = String(step.simulation_template_id || '').trim();
      if (!simulationTemplates.some((template) => template.id === templateId)) {
        return `Simulation template is unavailable on step "${step.title || step.order}"`;
      }
      const objective = String(step.simulation_objective || '').trim();
      if (!objective) {
        return `Student objective required on step "${step.title || step.order}"`;
      }
      if (objective.length > 500) {
        return `Student objective must be 500 characters or fewer on step "${step.title || step.order}"`;
      }
      return null;
    })
    .filter(Boolean) as string[];
  const signalIssues = steps
    .filter((step) => step.activity_format !== 'simulation')
    .filter((step) => (step.live_response_mode && step.live_response_mode !== 'none') || Object.keys(step.signal_config || {}).length > 0)
    .map((step) => {
      const sc = normalizeSignalConfigForSave(step);
      const type = sc.type;
      if (Object.keys(sc).length === 0) {
        return `Response enabled but missing signal config on step "${step.title || step.order}"`;
      }
      if (requiresSignalPrompt(sc.type)) {
        if (!sc.prompt || !String(sc.prompt).trim()) {
          return `Signal prompt required on step "${step.title || step.order}"`;
        }
      }

      if (String(type || '').toLowerCase() === 'mcq') {
        const options = (step.signal_config?.options || [])
          .map((opt: any) => typeof opt === 'string' ? opt.trim() : (opt?.text || '').trim())
          .filter(Boolean);
        if (options.length < 2) {
          return `MCQ needs at least 2 options on step "${step.title || step.order}"`;
        }
      }
      return null;
    })
    .filter(Boolean) as string[];
  const runnerPhaseIssues = (() => {
    const orderedSteps = [...steps].sort((a, b) => {
      const orderA = Number(a.order || 0);
      const orderB = Number(b.order || 0);
      if (orderA !== orderB) return orderA - orderB;
      return String(a.id).localeCompare(String(b.id));
    });
    let studioSeen = false;
    for (const step of orderedSteps) {
      const runnerPhase = String(step.runner_phase || 'live').trim().toLowerCase() || 'live';
      if (runnerPhase === 'studio') {
        studioSeen = true;
        continue;
      }
      if (studioSeen) {
        return ['Independent Work steps must form one contiguous tail at the end of the lesson.'];
      }
    }
    return [] as string[];
  })();
  const artifactIssues = steps
    .map((step) => {
      if (step.artifact_requirement !== 'apply_required') return null;
      if (step.stage_tag !== 'apply') {
        return `Official artifact only allowed on APPLY for step "${step.title || step.order}"`;
      }
      if (!step.artifact_location || step.artifact_location === 'none') {
        return `Select a submission location on step "${step.title || step.order}"`;
      }
      if (step.artifact_location === 'headrush') {
        const externalUrl = getArtifactExternalUrl(step).trim();
        const externalAssignmentId = getArtifactExternalAssignmentId(step).trim();
        if (!externalUrl && !externalAssignmentId) {
          return `Headrush steps need a URL or assignment reference on step "${step.title || step.order}"`;
        }
      }
      return null;
    })
    .filter(Boolean) as string[];
  const resourceIssues = resources
    .map((resource, index) => {
      const issue = lessonMaterialIssue(resource, steps.map((step) => Number(step.order)));
      return issue ? `Material ${index + 1}: ${issue}` : null;
    })
    .filter(Boolean) as string[];

  const handleSavePlan = async () => {
    if (!planId) return;
    for (const step of steps) {
      const rows = step.content?.authoring_pairs as Array<[string, string]> | undefined;
      if (rows && (rows.some(([a,b]) => !a.trim() || !b.trim()) || new Set(rows.map(([a]) => a.trim())).size !== rows.length)) {
        setSaveError(`Complete each matching pair and use unique terms in ${step.title}.`);
        return;
      }
    }
    if (isDeletingStep) {
      setSaveError('A step is still deleting. Please wait.');
      return;
    }
    const hasSourceItemJsonError = Object.values(sourceItemJsonErrors).some((msg) => Boolean(msg));
    if (hasSourceItemJsonError) {
      setSaveError('Fix Structured Source Item JSON before saving.');
      return;
    }
    if (evidenceBlocked) {
      setSaveError('WHY steps cannot collect evidence. Disable evidence before saving.');
      return;
    }
    if (simulationIssues.length > 0) {
      setSaveError(simulationIssues[0]);
      return;
    }
    if (signalIssues.length > 0) {
      setSaveError(signalIssues[0]);
      return;
    }
    if (runnerPhaseIssues.length > 0) {
      setSaveError(runnerPhaseIssues[0]);
      return;
    }
    if (artifactIssues.length > 0) {
      setSaveError(artifactIssues[0]);
      return;
    }
    if (resourceIssues.length > 0) {
      setSaveError(resourceIssues[0]);
      return;
    }
    setSaveError('');
    setSaveStatus('Saving...');

    try {
      if (planDirty) {
        await lessonPlansApi.update(Number(planId), {
          hook: hookId ? Number(hookId) : null,
          title,
          description,
          content_format: contentFormat,
          hook_level: hookLevel,
          week_number: weekNumber ? Number(weekNumber) : null,
          default_pacing: defaultPacing,
          is_template: isTemplate,
          resources,
        });
      }

      const draftDirtyStepIds = new Set<number | string>();
      const stepsForSave = steps.map((step) => {
        const key = String(step.id);
        const rawDraft = sourceItemJsonDraft[key];
        if (typeof rawDraft !== 'string' || !rawDraft.trim() || sourceItemJsonErrors[key]) {
          return step;
        }
        try {
          const parsed = JSON.parse(rawDraft || '{}');
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return step;
          const currentContent = step.content && typeof step.content === 'object' ? step.content as Record<string, any> : {};
          const currentSourceItem = currentContent.source_item ?? currentContent.sourceItem ?? {};
          if (JSON.stringify(currentSourceItem) === JSON.stringify(parsed)) return step;
          const restContent = { ...currentContent };
          delete restContent.sourceItem;
          draftDirtyStepIds.add(step.id);
          return {
            ...step,
            content: normalizeStructuredPromptContent({
              ...restContent,
              source_item: parsed,
            }),
          };
        } catch {
          return step;
        }
      });

      let updatedSteps = [...stepsForSave];

      for (const step of stepsForSave) {
        if (typeof step.id !== 'number') {
          const createPayload = step.activity_format === 'simulation'
            ? {
                title: step.title,
                order: step.order,
                stage_tag: step.stage_tag,
                runner_phase: step.runner_phase || 'live',
                activity_format: 'simulation',
                completion_role: step.completion_role || 'required',
                simulation_template_id: step.simulation_template_id,
                simulation_objective: String(step.simulation_objective || '').trim(),
                content: {
                  pacing_timer: normalizeStepPacingTimer(step.content),
                },
              }
            : {
                ...step,
                content: normalizeStructuredPromptContent(step.content),
                signal_config: normalizeSignalConfigForSave(step),
                activity_format: 'standard',
                simulation_template_id: undefined,
                simulation_objective: undefined,
              };
          const response = await lessonPlansApi.addStep(Number(planId), createPayload);
          setSelectedStepId(current => current === step.id ? response.data.id : current);
          updatedSteps = updatedSteps.map((item) =>
            item.id === step.id ? { ...step, id: response.data.id } : item
          );
        }
      }

      const dirtyExistingSteps = updatedSteps
        .filter((step) => typeof step.id === 'number' && (dirtySteps.has(step.id) || draftDirtyStepIds.has(step.id)))
        .sort((a, b) => {
          const phaseA = a.runner_phase || 'live';
          const phaseB = b.runner_phase || 'live';
          const orderA = Number(a.order ?? 0);
          const orderB = Number(b.order ?? 0);
          if (phaseA === 'studio' && phaseB === 'studio') {
            return (orderB - orderA) || (Number(b.id) - Number(a.id));
          }
          if (phaseA === 'studio') return 1;
          if (phaseB === 'studio') return -1;
          return (orderA - orderB) || (Number(a.id) - Number(b.id));
        });

      for (const step of dirtyExistingSteps) {
        if (step.activity_format === 'simulation') {
          await lessonPlansApi.updateStep(Number(step.id), {
            title: step.title,
            order: step.order,
            stage_tag: step.stage_tag,
            runner_phase: step.runner_phase || 'live',
            activity_format: 'simulation',
            completion_role: step.completion_role || 'required',
            simulation_template_id: step.simulation_template_id,
            simulation_objective: String(step.simulation_objective || '').trim(),
            content: {
              pacing_timer: normalizeStepPacingTimer(step.content),
            },
          });
          continue;
        }
        const signalConfig = normalizeSignalConfigForSave(step);
        if (signalConfig.type === 'mcq' && Array.isArray(signalConfig.options)) {
          signalConfig.options = signalConfig.options.map((opt: any) => {
            if (typeof opt === 'string') return opt.trim();
            if (typeof opt === 'object' && opt !== null && typeof opt.text === 'string') {
              return { ...opt, text: opt.text.trim() };
            }
            return opt;
          }).filter(Boolean);
        }
        const content = step.content && typeof step.content === 'object'
          ? (() => {
              const rawContent = step.content as Record<string, any>;
              const { sourceItem, ...rest } = rawContent;
              const canonicalContent: Record<string, any> = { ...rest };
              const sourceItemValue = canonicalContent.source_item ?? sourceItem;
              if (sourceItemValue && typeof sourceItemValue === 'object') {
                canonicalContent.source_item = sourceItemValue;
                const sourceType = String((sourceItemValue as any).type || '').trim().toLowerCase();
                const structuredSignalTypes = ['pairs', 'matching', 'ordering', 'drag_drop', 'drag_drop_code', 'drag_drop_fill_blanks', 'drag_drop_keywords'];
                if (structuredSignalTypes.includes(sourceType)) {
                  return normalizeStructuredPromptContent(canonicalContent);
                }
              }
              return canonicalContent;
            })()
          : step.content;
        if (isDev) {
          console.debug('[LessonPlanEditor] updateStep payload content', {
            stepId: step.id,
            blocks: Array.isArray((content as any)?.blocks) ? (content as any).blocks.length : 0,
            hasPromptMd: Boolean(typeof (content as any)?.prompt_md === 'string' && (content as any).prompt_md.trim()),
          });
        }
        await lessonPlansApi.updateStep(Number(step.id), {
          title: step.title,
          order: step.order,
          stage_tag: step.stage_tag,
          runner_phase: step.runner_phase || 'live',
          step_kind: step.step_kind,
          counts_as_evidence: step.counts_as_evidence,
          live_response_mode: step.live_response_mode || 'none',
          completion_role: step.completion_role || 'required',
          artifact_requirement: step.artifact_requirement || 'none',
          artifact_location: step.artifact_requirement === 'apply_required'
            ? (step.artifact_location || 'headrush')
            : 'none',
          artifact_config: step.artifact_requirement === 'apply_required'
            ? getArtifactConfig(step)
            : {},
          coach_mode: step.artifact_requirement === 'apply_required'
            ? (step.coach_mode || 'off')
            : 'off',
          content,
          evidence_config: step.evidence_config,
          signal_config: signalConfig,
          activity_format: 'standard',
        });
      }

      if (orderDirty) {
        const orderedIds = updatedSteps
          .filter((step) => typeof step.id === 'number')
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((step) => step.id as number);
        await lessonPlansApi.reorderSteps(Number(planId), orderedIds);
      }

      setSteps(updatedSteps);
      setDirtySteps(new Set());
      setPlanDirty(false);
      setOrderDirty(false);
      setSaveStatus('Saved');
      window.setTimeout(() => setSaveStatus(''), 1500);
    } catch (err: any) {
      setSaveStatus('');
      setSaveError(extractApiErrorMessage(err, 'Failed to save lesson plan.'));
    }
  };

  const selectedBlocks = useMemo(() => (selectedStep ? normalizeBlocks(selectedStep.content) : []), [selectedStep]);
  const teacherNotes = selectedStep ? getTeacherNotes(selectedStep.content) : '';
  const quizConfig = selectedStep ? getQuizConfig(selectedStep.content) : getQuizConfig({});

  const applyContentUpdate = (blocks: StepBlock[], notes: string, quizCfg = quizConfig) => {
    if (!selectedStep) return;
    const nextContent = buildContent(selectedStep.content, blocks, notes, quizCfg, selectedStep.step_kind === 'quiz');
    updateStep(selectedStep.id, { content: nextContent });
  };

  const handleCopyAsMarkdown = async () => {
    const markdown = formatLessonPlanMarkdown(
      {
        id: planId || '',
        title,
        hookId,
        hookTitle,
      },
      steps,
    );
    try {
      await navigator.clipboard.writeText(markdown);
      setCopyMarkdownStatus('copied');
    } catch {
      setCopyMarkdownStatus('error');
    }
    window.setTimeout(() => setCopyMarkdownStatus('idle'), 1500);
  };

  const warningMessages = (warnings: any): string[] => {
    if (!Array.isArray(warnings)) return [];
    return warnings
      .map((warning) => {
        if (typeof warning === 'string') return warning;
        if (warning && typeof warning === 'object' && typeof warning.message === 'string') {
          return warning.message;
        }
        return '';
      })
      .filter(Boolean);
  };

  const resetImportModal = () => {
    setImportFile(null);
    setImportText('');
    setImportMode('append');
    setImportError('');
    setImportWarnings([]);
    setImportPreview(null);
    setImportPreviewBundle(null);
    setImportPreviewLoading(false);
  };

  const clearImportPreview = () => {
    setImportPreview(null);
    setImportPreviewBundle(null);
    setImportWarnings([]);
  };

  const parseImportBundleInput = async (): Promise<{ bundle: Record<string, any>; source: 'file' | 'text' }> => {
    if (importFile) {
      const rawText = await importFile.text();
      const normalizedText = rawText.replace(/^\uFEFF/, '').trim();
      if (!normalizedText) {
        throw new Error('Selected file is empty.');
      }
      let bundle;
      try {
        bundle = JSON.parse(normalizedText);
      } catch {
        throw new Error('Invalid JSON in uploaded file.');
      }
      if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
        throw new Error('Bundle payload must be a JSON object.');
      }
      return { bundle, source: 'file' };
    }

    const text = importText.trim();
    if (!text) {
      throw new Error('Upload a .json file or paste bundle JSON.');
    }
    let bundle;
    try {
      bundle = JSON.parse(text);
    } catch {
      throw new Error('Invalid JSON in text area.');
    }
    if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
      throw new Error('Bundle payload must be a JSON object.');
    }
    return { bundle, source: 'text' };
  };

  const handlePreviewImport = async () => {
    if (!planId) return;
    if (isDirty) {
      setImportError('Save current changes before importing steps.');
      return;
    }

    setImportPreviewLoading(true);
    setImportError('');
    setImportWarnings([]);
    try {
      const { bundle, source } = await parseImportBundleInput();
      const bundleType = bundle.bundle_type;
      if (bundleType !== undefined && bundleType !== 'LessonPlanImportBundle') {
        throw new Error('bundle_type must be "LessonPlanImportBundle".');
      }
      const bundleVersion = bundle.bundle_version;
      if (bundleVersion !== undefined && bundleVersion !== 'v2') {
        throw new Error('bundle_version must be "v2".');
      }

      const stepsRaw = bundle.steps;
      if (!Array.isArray(stepsRaw)) {
        throw new Error('steps must be a list.');
      }

      const existingRows: ImportPreviewExistingRow[] = [...sortedSteps]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((step, idx) => {
          const rawOrder = Number(step.order);
          return {
            order: Number.isFinite(rawOrder) ? rawOrder : idx,
            title: String(step.title || '').trim() || `Untitled ${idx + 1}`,
            stageTag: String(step.stage_tag || '').trim().toLowerCase() || 'define',
          };
        });
      const existingById = new Map<number, ImportPreviewExistingRow>();
      sortedSteps.forEach((step, idx) => {
        if (typeof step.id !== 'number') return;
        const rawOrder = Number(step.order);
        existingById.set(step.id, {
          order: Number.isFinite(rawOrder) ? rawOrder : idx,
          title: String(step.title || '').trim() || `Untitled ${idx + 1}`,
          stageTag: String(step.stage_tag || '').trim().toLowerCase() || 'define',
        });
      });
      const baseOrder = existingRows.length > 0 ? Math.max(...existingRows.map((row) => row.order)) + 1 : 0;
      const warningSet = new Set<string>();
      if (stepsRaw.length === 0) {
        warningSet.add('Bundle contains no steps.');
      }
      if (hookId) {
        const requestedHookId = Number(bundle?.plan?.hook);
        const currentHookId = Number(hookId);
        if (Number.isFinite(requestedHookId) && Number.isFinite(currentHookId) && requestedHookId !== currentHookId) {
          warningSet.add('Bundle hook does not match this plan hook. This plan will remain the target.');
        }
      }

      const parsedRows = stepsRaw.map((stepRaw, index) => {
        if (!stepRaw || typeof stepRaw !== 'object' || Array.isArray(stepRaw)) {
          throw new Error(`steps[${index}] must be an object.`);
        }
        const rawOrder = Number((stepRaw as any).order);
        const hasRequestedOrder = Number.isFinite(rawOrder);
        const requestedOrder = hasRequestedOrder ? rawOrder : index;
        const targetStepId = getImportTargetStepId(stepRaw);
        const targetRow = targetStepId ? existingById.get(targetStepId) : null;
        if (importMode === 'update' && targetStepId && !targetRow) {
          throw new Error(`steps[${index}] target step ${targetStepId} is not in this plan.`);
        }
        return {
          action: importMode === 'update' && targetStepId ? 'update' as const : 'create' as const,
          sourceIndex: index,
          requestedOrder,
          hasRequestedOrder,
          targetStepId,
          targetRow,
          title: String((stepRaw as any).title || '').trim() || `Untitled ${index + 1}`,
          stageTag: String((stepRaw as any).stage_tag || (stepRaw as any).stageTag || 'define').trim().toLowerCase() || 'define',
          stepKind: String((stepRaw as any).step_kind || (stepRaw as any).stepKind || 'instruction').trim().toLowerCase() || 'instruction',
        };
      });
      const createRows = parsedRows.filter((row) => row.action === 'create');
      const updateRows = parsedRows.filter((row) => row.action === 'update');
      if (importMode === 'update' && createRows.length > 0) {
        warningSet.add('Incoming steps without an existing step id will be appended.');
      }

      const orderCounts = new Map<number, number>();
      createRows.forEach((row) => {
        orderCounts.set(row.requestedOrder, (orderCounts.get(row.requestedOrder) || 0) + 1);
      });
      const duplicateOrders = Array.from(orderCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([order]) => order)
        .sort((a, b) => a - b);
      if (duplicateOrders.length > 0) {
        warningSet.add(`Duplicate requested order values (${duplicateOrders.join(', ')}) will be normalized.`);
      }

      const incomingRows = [...createRows]
        .sort((a, b) => (a.requestedOrder - b.requestedOrder) || (a.sourceIndex - b.sourceIndex))
        .map((row, index) => ({
          ...row,
          finalOrder: baseOrder + index,
        }));
      const createFinalOrderBySourceIndex = new Map<number, number>();
      incomingRows.forEach((row) => {
        createFinalOrderBySourceIndex.set(row.sourceIndex, row.finalOrder);
      });
      const previewRows = [...parsedRows]
        .map((row) => ({
          ...row,
          finalOrder: row.action === 'update'
            ? (row.hasRequestedOrder ? row.requestedOrder : row.targetRow?.order ?? row.sourceIndex)
            : createFinalOrderBySourceIndex.get(row.sourceIndex) ?? row.requestedOrder,
        }))
        .sort((a, b) => (a.finalOrder - b.finalOrder) || (a.sourceIndex - b.sourceIndex));

      setImportPreviewBundle(bundle);
      setImportPreview({
        mode: importMode,
        source,
        existingCount: existingRows.length,
        incomingCount: parsedRows.length,
        createdCount: createRows.length,
        updatedCount: updateRows.length,
        finalCount: existingRows.length + createRows.length,
        existingRows,
        incomingRows: previewRows,
        warnings: Array.from(warningSet),
      });
    } catch (err: any) {
      setImportPreview(null);
      setImportPreviewBundle(null);
      setImportError(String(err?.message || 'Unable to preview import.'));
    } finally {
      setImportPreviewLoading(false);
    }
  };

  const handleImportSteps = async () => {
    if (!planId) return;
    if (isDirty) {
      setImportError('Save current changes before importing steps.');
      return;
    }
    if (!importPreview) {
      setImportError('Preview import before applying.');
      return;
    }

    setImportLoading(true);
    setImportError('');
    setImportWarnings([]);
    try {
      let response;
      if (importFile) {
        response = await lessonPlansApi.importBundle({ file: importFile, targetPlanId: Number(planId), importMode });
      } else {
        const bundle = importPreviewBundle;
        if (!bundle || typeof bundle !== 'object') {
          setImportError('Preview import before applying.');
          return;
        }
        response = await lessonPlansApi.importBundle({ bundle, targetPlanId: Number(planId), importMode });
      }

      const warnings = warningMessages(response?.data?.warnings);
      setImportWarnings(warnings);
      await refreshStepsFromServer();
      const createdCount = Number(response?.data?.created_count ?? Object.keys(response?.data?.step_id_map || {}).length);
      const updatedCount = Number(response?.data?.updated_count ?? 0);
      const statusParts = importMode === 'update'
        ? [
          updatedCount > 0 ? `updated ${updatedCount}` : '',
          createdCount > 0 ? `added ${createdCount}` : '',
        ].filter(Boolean)
        : [`imported ${createdCount}`];
      setSaveStatus(statusParts.length > 0 ? statusParts.join(', ') : 'Import applied');
      window.setTimeout(() => setSaveStatus(''), 1500);
      if (warnings.length === 0) {
        resetImportModal();
        setImportModalOpen(false);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const fileError = Array.isArray(data?.file) ? data.file.join(' ') : '';
      const detailError = typeof data?.detail === 'string' ? data.detail : '';
      const targetPlanError = Array.isArray(data?.target_plan_id) ? data.target_plan_id.join(' ') : '';
      setImportError(fileError || targetPlanError || detailError || data?.error || 'Import failed.');
    } finally {
      setImportLoading(false);
    }
  };

  const updateStructuredSourceItem = (updater: (sourceItem: Record<string, any>) => Record<string, any>) => {
    if (!selectedStep) return;
    const currentContent = selectedStep.content && typeof selectedStep.content === 'object' ? selectedStep.content : {};
    const sourceItemValue = currentContent.source_item ?? currentContent.sourceItem;
    const currentSourceItem = sourceItemValue && typeof sourceItemValue === 'object' ? sourceItemValue : {};
    const nextSourceItem = updater({ ...currentSourceItem });
    const { sourceItem, ...restContent } = currentContent as Record<string, any>;
    const nextContent = normalizeStructuredPromptContent({
      ...restContent,
      source_item: nextSourceItem,
    });
    updateStep(selectedStep.id, { content: nextContent });
  };

  useEffect(() => {
    if (!selectedStepId || !selectedStep) return;
    const sourceItem = selectedStep.content?.source_item && typeof selectedStep.content?.source_item === 'object'
      ? selectedStep.content.source_item
      : null;
    const sourceType = String(sourceItem?.type || '').trim().toLowerCase();
    if (sourceType !== 'matching') return;
    const rows = normalizeMatchingDraftRows(
      sourceItem?.pairs && typeof sourceItem.pairs === 'object' ? sourceItem.pairs : {}
    );
    const nextRows = rows.length > 0 ? rows : [{ left: '', right: '' }];
    setMatchingDraftRows((prev) => ({ ...prev, [String(selectedStepId)]: nextRows }));
  }, [selectedStepId, selectedStep]);

  useEffect(() => {
    if (!selectedStepId || !selectedStep) return;
    const key = String(selectedStep.id);
    const sourceItem = selectedStep.content?.source_item
      ?? selectedStep.content?.sourceItem
      ?? selectedStep.signal_config?.source_item
      ?? selectedStep.signal_config?.sourceItem;
    const signalType = String(selectedStep.signal_config?.signal_type || selectedStep.signal_config?.type || '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '_');
    const structuredSignalTypes = ['pairs', 'matching', 'ordering', 'drag_drop', 'drag_drop_code', 'drag_drop_fill_blanks', 'drag_drop_keywords'];
    if (sourceItem && typeof sourceItem === 'object') {
      setSourceItemJsonDraft((prev) => ({ ...prev, [key]: JSON.stringify(sourceItem, null, 2) }));
      setSourceItemJsonErrors((prev) => ({ ...prev, [key]: '' }));
      return;
    }
    if (structuredSignalTypes.includes(signalType)) {
      setSourceItemJsonDraft((prev) => ({ ...prev, [key]: '{}' }));
      setSourceItemJsonErrors((prev) => ({ ...prev, [key]: '' }));
    }
  }, [selectedStepId, selectedStep]);

  const pacingTimerEditor = selectedStep ? (
    <div className="space-y-3 rounded-lg border border-sky-100 bg-sky-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-sky-800">Pacing timer</p>
          <p className="mt-1 text-xs text-sky-900/80">
            Show a shared countdown on live runner screens for time-boxed activities.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-sky-950">
          <input
            type="checkbox"
            checked={selectedStepPacingTimer.enabled}
            onChange={(e) => updateStepPacingTimer(selectedStep.id, (currentTimer) => ({
              ...currentTimer,
              enabled: e.target.checked,
              duration_seconds: currentTimer.duration_seconds > 0 ? currentTimer.duration_seconds : 300,
            }))}
          />
          Enable pacing timer
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-sky-800">Timer label</label>
          <input
            aria-label="Timer label"
            className="w-full rounded border px-3 py-2 text-sm"
            value={selectedStepPacingTimer.label}
            onChange={(e) => updateStepPacingTimer(selectedStep.id, (currentTimer) => ({
              ...currentTimer,
              label: e.target.value,
            }))}
            placeholder="Troubleshooting practice"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-sky-800">Timer length (minutes)</label>
          <input
            aria-label="Timer length (minutes)"
            type="number"
            min={1}
            step={1}
            className="w-full rounded border px-3 py-2 text-sm"
            value={selectedStepPacingTimer.duration_seconds > 0 ? String(Math.max(1, Math.round(selectedStepPacingTimer.duration_seconds / 60))) : ''}
            onChange={(e) => {
              const nextMinutes = Number(e.target.value || 0);
              updateStepPacingTimer(selectedStep.id, (currentTimer) => ({
                ...currentTimer,
                duration_seconds: Number.isFinite(nextMinutes) && nextMinutes > 0 ? Math.round(nextMinutes) * 60 : 0,
              }));
            }}
            placeholder="5"
          />
        </div>
      </div>
      <p className="text-xs text-sky-900/80">
        Best for live steps. Independent Work remains student-paced and usually should not use the timer.
      </p>
    </div>
  ) : null;

  const controlledDeliveryContent = assessmentSource ? (
    <div className={redesignEnabled ? 'min-h-[calc(100vh-10rem)] bg-bg' : 'min-h-screen bg-gray-50'}>
      <header className="border-b border-indigo-200 bg-indigo-50">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700">Assessment delivery copy</p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-950">{title}</h1>
            <p className="mt-1 text-sm text-indigo-900">
              Generated from {assessmentSource.assessment_title}, published version {assessmentSource.version_number}.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => navigate(`/v2/teacher/assessments?assessmentId=${assessmentSource.assessment_id}`)}
            >
              Edit source assessment
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => window.open(`/v2/teacher/lesson-plan/${planId}/student-preview`, '_blank', 'noopener')}
            >
              Preview as Student
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={returnToLibrary}
            >
              Back to Library
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-5 px-6 py-6">
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          <h2 className="font-semibold">Assessment content is controlled here</h2>
          <p className="mt-1">
            Questions, answer keys, and evidence requirements cannot be edited in the Lesson Runner editor. Edit the source assessment and publish a new version instead.
          </p>
          <p className="mt-2 text-amber-900">
            Student assignment, dates, availability, and run controls remain available from the normal teaching and assignment screens.
          </p>
        </section>
        <section className="rounded-lg border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-gray-950">Published assessment items</h2>
              <p className="text-sm text-gray-600">Read-only delivery preview · {sortedSteps.length} item{sortedSteps.length === 1 ? '' : 's'}</p>
            </div>
            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-800">
              Version {assessmentSource.version_number}
            </span>
          </div>
          <div className="mt-4 space-y-3">
            {sortedSteps.map((step, index) => (
              <article key={step.id} className="rounded border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-gray-900">{index + 1}. {step.title}</h3>
                  <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    {String((step.content as any)?.assessment_item_type || step.step_kind || 'assessment').replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                  {String((step.content as any)?.prompt_md || step.signal_config?.prompt || 'Assessment item')}
                </p>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  ) : null;

  const editorContent = (
    <div className={redesignEnabled ? 'h-[calc(100vh-10rem)] overflow-hidden bg-bg flex flex-col' : 'h-screen overflow-hidden bg-gray-50 flex flex-col'}>
      <header className={redesignEnabled ? 'sticky top-0 z-10 bg-surface border-b border-border' : 'sticky top-0 z-10 bg-white border-b border-gray-200'}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap gap-3 items-center justify-between">
          <div>
            <input
              className="text-2xl font-semibold text-gray-900 bg-transparent focus:outline-none"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setPlanDirty(true);
              }}
              placeholder="Lesson plan title"
            />
            <details open={!redesignEnabled || advancedEditor}>
            <summary className="cursor-pointer text-sm text-gray-600">Lesson organization / Hook alignment</summary>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                Content format
                <select aria-label="Content format" value={contentFormat} onChange={e => { setContentFormat(e.target.value as 'lesson' | 'activity'); setPlanDirty(true); }}><option value="lesson">Full lesson</option><option value="activity">Activity</option></select>
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                Hook level
                <select aria-label="Lesson plan Hook level" className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800"
                  value={hookLevel} onChange={(event) => { setHookLevel(event.target.value); setPlanDirty(true); }}>
                  <option value="">Not assigned</option>
                  {['define', 'do', 'explain', 'apply'].map((level) => <option key={level} value={level}>{level.toUpperCase()}</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                Curriculum week
                <select
                  aria-label="Curriculum week"
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800"
                  value={weekNumber}
                  onChange={(event) => {
                    setWeekNumber(event.target.value);
                    setPlanDirty(true);
                  }}
                >
                  <option value="">Not set</option>
                  {Array.from({ length: 52 }, (_, index) => index + 1).map((week) => (
                    <option key={week} value={week}>Week {week}</option>
                  ))}
                </select>
              </label>
              <div className="text-xs text-gray-500">
                {isDirty ? 'Unsaved changes ●' : 'Saved'} {saveStatus && `· ${saveStatus}`}
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">Hook level groups this plan’s current and historical student evidence. Step labels still describe instruction.</p>
            </details>
            {redesignEnabled && !advancedEditor && <p className="text-xs text-gray-500">{isDirty ? 'Unsaved changes ●' : 'Saved'} {saveStatus && `· ${saveStatus}`}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={returnToLibrary}
            >
              Back to Library
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPreviewOpen(true)}
            >
              Preview Step
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (planId) {
                  window.open(`/v2/teacher/lesson-plan/${planId}/student-preview`, '_blank', 'noopener');
                }
              }}
              disabled={!planId || isDirty}
              title={isDirty ? 'Save changes before opening the student preview.' : 'Open the whole lesson as a student before starting a live run.'}
            >
              Preview as Student
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCopyAsMarkdown}
              disabled={!planId}
            >
              Copy as Markdown
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                resetImportModal();
                setImportModalOpen(true);
              }}
              disabled={!planId || isDeletingStep || isDirty}
              title={isDirty ? 'Save changes before importing steps.' : 'Import or update steps in this plan'}
            >
              Import / Update
            </Button>
            {copyMarkdownStatus === 'copied' && <span className="text-xs text-green-700">Copied!</span>}
            {copyMarkdownStatus === 'error' && <span className="text-xs text-red-700">Copy failed</span>}
            <Button
              type="button"
              onClick={handleSavePlan}
              disabled={!planId || isDeletingStep}
            >
              Save Plan
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl w-full mx-auto px-6 py-4 flex-1 overflow-hidden">
        <div className={redesignEnabled ? 'flex flex-col xl:flex-row h-full overflow-auto gap-6' : 'flex h-[calc(100vh-160px)] overflow-hidden gap-6'}>
        <section className="w-full xl:w-[360px] shrink-0 overflow-y-auto pr-2 space-y-4">
          <details open={materialsOpen} onToggle={e => setMaterialsOpen(e.currentTarget.open)}>
          <summary className="cursor-pointer font-medium">Lesson materials / Drive slides</summary>
          <LessonMaterialsEditor
            lessonPlanId={planId ? Number(planId) : undefined}
            resources={resources}
            steps={sortedSteps.map((step) => ({ order: Number(step.order), title: step.title }))}
            onChange={(nextResources) => {
              setResources(nextResources);
              setPlanDirty(true);
            }}
          />
          </details>
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{redesignEnabled ? 'Lesson activities' : 'Steps'}</h2>
            <div className="flex flex-wrap gap-2">
              <button className="px-3 py-2 rounded border border-gray-200" onClick={() => redesignEnabled ? setActivityPickerOpen(!activityPickerOpen) : addStep()}>
                {redesignEnabled ? '+ Add activity' : '+ Add Step'}
              </button>
              <button className="px-3 py-2 rounded border border-gray-200" onClick={duplicateStep} disabled={!selectedStep}>
                Duplicate
              </button>
              <details className="relative">
                <summary className="list-none cursor-pointer rounded border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-800">
                  + Routine
                </summary>
                <div className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-violet-200 bg-white p-2 shadow-lg">
                  <p className="px-2 pb-2 text-xs text-slate-500">Add editable, timed live steps. Timers begin only after the teacher opens the prompt and selects Start.</p>
                  {(Object.keys(urgencyPresetLabels) as UrgencyPresetKey[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="block w-full rounded px-3 py-2 text-left text-sm text-slate-800 hover:bg-violet-50"
                      onClick={(event) => {
                        addUrgencyPreset(key);
                        const details = event.currentTarget.closest('details');
                        if (details) details.removeAttribute('open');
                      }}
                    >
                      {urgencyPresetLabels[key]}
                    </button>
                  ))}
                </div>
              </details>
            </div>
          </div>
          {activityPickerOpen && <div className="grid gap-2" aria-label="Choose activity format">{activityPresets.map(([type,label,description]) => <button key={type} className="rounded-lg border border-border bg-surface p-3 text-left disabled:opacity-50" disabled={type === 'simulation' && !simulationTemplates.length} onClick={() => addStep(type)}><span className="block font-medium">{label}</span><span className="text-xs text-muted">{type === 'simulation' && !simulationTemplates.length ? 'No simulation templates available' : description}</span></button>)}</div>}
          <div className="space-y-2">
            {sortedSteps.map((step) => (
              <div
                key={step.id}
                className={`rounded-lg border p-3 bg-white ${selectedStepId === step.id ? 'border-indigo-500 ring-1 ring-indigo-200' : 'border-gray-200'}`}
                onClick={() => setSelectedStepId(step.id)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setSelectedStepId(step.id); } }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">{step.order}</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{step.title || 'Untitled step'}</p>
                    {redesignEnabled && <p className="text-xs text-muted">{activityLabel(step)}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {dirtySteps.has(step.id) && <span className="text-indigo-500">●</span>}
                    <button
                      className="text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStepId(step.id);
                        setPreviewOpen(true);
                      }}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button className="text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); moveStep(step.id, -1); }}>
                      <ChevronUpIcon className="h-4 w-4" />
                    </button>
                    <button className="text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); moveStep(step.id, 1); }}>
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase">{step.stage_tag || '—'}</span>
                  {step.runner_phase === 'studio' && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Independent work</span>
                  )}
                  {step.artifact_requirement === 'apply_required' && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Official artifact</span>
                  )}
                  {step.activity_format === 'simulation' && (
                    <span className="px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700">Simulation</span>
                  )}
                  {step.activity_format !== 'simulation'
                  && ((step.live_response_mode && step.live_response_mode !== 'none') || Object.keys(step.signal_config || {}).length > 0) && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {step.runner_phase === 'studio' ? 'Response' : 'Live response'}
                    </span>
                  )}
                  {normalizeStepPacingTimer(step.content).enabled && step.runner_phase !== 'studio' && (
                    <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Timer</span>
                  )}
                  {step.counts_as_evidence && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Legacy evidence</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="min-w-0 flex-1 overflow-y-auto">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
          {!selectedStep && <p className="text-sm text-gray-500">Select a step to edit.</p>}
          {redesignEnabled && !advancedEditor && saveError && <p role="alert" className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700">{saveError}</p>}
          {selectedStep && redesignEnabled && <div className="mb-4 flex justify-between gap-3"><button className="btn-secondary" onClick={() => setAdvancedEditor(!advancedEditor)}>{advancedEditor ? 'Back to simple editor' : 'Full settings'}</button><button className="text-sm text-red-600" onClick={() => setConfirmDeleteOpen(true)}>Delete activity</button></div>}
          {selectedStep && redesignEnabled && !advancedEditor && <SimpleActivityEditor key={selectedStep.id} step={selectedStep} blocks={selectedBlocks} notes={teacherNotes} onUpdate={patch => updateStep(selectedStep.id, patch)} onContent={applyContentUpdate} onAdvanced={() => setAdvancedEditor(true)} onPreview={() => setPreviewOpen(true)} onMaterials={() => setMaterialsOpen(true)} templates={simulationTemplates} />}
          {selectedStep && (!redesignEnabled || advancedEditor) && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-4">
                <input
                  className="flex-1 border rounded px-3 py-2 text-lg font-semibold"
                  value={selectedStep.title}
                  onChange={(e) => updateStep(selectedStep.id, { title: e.target.value })}
                  placeholder="Step title"
                />
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={selectedStep.stage_tag || ''}
                  onChange={(e) => updateStep(selectedStep.id, { stage_tag: e.target.value })}
                >
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!selectedStepIsSimulation && (
                  <select
                    className="border rounded px-3 py-2 text-sm"
                    aria-label="Step kind"
                    value={selectedStep.step_kind || 'instruction'}
                    onChange={(e) => updateStep(selectedStep.id, { step_kind: e.target.value })}
                  >
                    {selectedStep.step_kind === 'quiz' && (
                      <option value="quiz" disabled>quiz (legacy configuration)</option>
                    )}
                    {stepKindOptions.map((kind) => (
                      <option key={kind} value={kind}>
                        {kind}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  className="border rounded px-3 py-2 text-sm"
                  aria-label="Lesson phase"
                  value={selectedStep.runner_phase || 'live'}
                  onChange={(e) => updateStep(selectedStep.id, { runner_phase: e.target.value as EditorStep['runner_phase'] })}
                >
                  {runnerPhaseOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button className="ml-auto text-sm text-red-500 flex items-center gap-1" onClick={() => setConfirmDeleteOpen(true)}>
                  <TrashIcon className="h-4 w-4" /> Delete Step
                </button>
              </div>

              <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-indigo-950">Activity format</label>
                    <select
                      className="w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm"
                      aria-label="Activity format"
                      value={selectedStepIsSimulation ? 'simulation' : 'standard'}
                      onChange={(e) => updateActivityFormat(
                        selectedStep,
                        e.target.value === 'simulation' ? 'simulation' : 'standard',
                      )}
                    >
                      <option value="standard">Standard</option>
                      <option
                        value="simulation"
                        disabled={simulationTemplates.length === 0 && !selectedStepIsSimulation}
                      >
                        Simulation
                      </option>
                    </select>
                    <p className="text-xs text-indigo-900/75">
                      Simulations use server-managed behavior and safety rules.
                    </p>
                  </div>
                  {selectedStepIsSimulation && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-indigo-950">Simulation template</label>
                      <select
                        className="w-full rounded border border-indigo-200 bg-white px-3 py-2 text-sm"
                        aria-label="Simulation template"
                        value={selectedStep.simulation_template_id || ''}
                        onChange={(e) => {
                          const template = simulationTemplates.find((item) => item.id === e.target.value);
                          if (!template) return;
                          updateStep(selectedStep.id, {
                            simulation_template_id: template.id,
                            simulation_objective: template.default_objective,
                          });
                        }}
                      >
                        {!simulationTemplates.some((item) => item.id === selectedStep.simulation_template_id) && (
                          <option value={selectedStep.simulation_template_id || ''}>
                            Unsupported template: {selectedStep.simulation_template_id || 'unknown'}
                          </option>
                        )}
                        {simulationTemplates.map((template) => (
                          <option key={template.id} value={template.id}>{template.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {simulationTemplatesError && (
                  <p className="mt-3 text-sm text-amber-800">{simulationTemplatesError}</p>
                )}
              </div>

              {selectedStepIsSimulation && (
                <div className="space-y-4 rounded-xl border border-cyan-200 bg-cyan-50 p-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-cyan-950">Student objective</label>
                    <textarea
                      className="min-h-[96px] w-full rounded border border-cyan-200 bg-white px-3 py-2 text-sm"
                      aria-label="Student objective"
                      maxLength={500}
                      value={selectedStep.simulation_objective || ''}
                      onChange={(e) => updateStep(selectedStep.id, {
                        simulation_objective: e.target.value,
                      })}
                    />
                    <p className="text-xs text-cyan-900/75">
                      Rules, actions, safety behavior, and completion requirements are controlled by the selected template.
                    </p>
                  </div>
                  {pacingTimerEditor}
                </div>
              )}

              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                <label className="block text-sm font-semibold text-indigo-950">Live completion role
                  <select className="mt-2 block w-full rounded border border-indigo-200 bg-white p-2 text-gray-900" value={selectedStep.completion_role || 'required'} onChange={event => updateStep(selectedStep.id, { completion_role: event.target.value as LessonStep['completion_role'] })}>
                    <option value="required">Required work — missing work affects completion</option>
                    <option value="check">Check for understanding — does not block completion</option>
                    <option value="optional">Optional practice — does not block completion</option>
                  </select>
                </label>
                <p className="mt-2 text-xs text-indigo-900">Applies to live response, evidence, and simulation steps. Instruction-only slides do not create a submission requirement. Required work can also provide learning evidence. Studio and standalone assessment requirements are separate. Changes apply to future runs; current runs have their own adjustments.</p>
              </div>

              {!selectedStepIsSimulation && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Lesson phase</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Live keeps the class teacher-paced. Independent Work is the Studio tail for deeper student-paced completion.
                      </p>
                    </div>
                    <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {selectedStep.runner_phase === 'studio' ? 'Independent Work' : 'Live'}
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Independent Work steps must be a contiguous tail at the end of the lesson.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <input
                        type="checkbox"
                        checked={selectedStepHasLiveResponse}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateStep(selectedStep.id, {
                              live_response_mode: selectedStep.live_response_mode && selectedStep.live_response_mode !== 'none'
                                ? selectedStep.live_response_mode
                                : getDefaultLiveResponseMode(selectedStep),
                              signal_config: Object.keys(selectedStep.signal_config || {}).length > 0
                                ? selectedStep.signal_config
                                : {
                                  type: 'text',
                                  prompt: '',
                                  reason_required: false,
                                  confidence_required: false,
                                  reasoning_gate: { mode: 'off', min_chars: 15 },
                                },
                            });
                          } else {
                            updateStep(selectedStep.id, {
                              live_response_mode: 'none',
                              signal_config: {},
                            });
                          }
                        }}
                      />
                      {selectedStepCollectResponseLabel}
                    </label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm"
                      aria-label="Live response mode"
                      value={selectedStep.live_response_mode || 'none'}
                      onChange={(e) => updateStep(selectedStep.id, { live_response_mode: e.target.value as EditorStep['live_response_mode'] })}
                      disabled={!selectedStepHasLiveResponse}
                    >
                      {liveResponseModeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">{selectedStepResponseHelpText}</p>
                    {selectedStepHasLiveResponse &&
                    normalizeSignalType(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type) !== 'guided_sequence' && (
                      <label className="flex items-start gap-2 rounded border border-gray-200 bg-white p-2 text-sm text-gray-800">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          aria-label="Require confidence rating"
                          checked={selectedStep.signal_config?.confidence_required === true}
                          onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => ({
                            ...currentSignalConfig,
                            confidence_required: e.target.checked,
                          }))}
                        />
                        <span>
                          <span className="block font-medium">Require confidence rating</span>
                          <span className="block text-xs text-gray-500">Turn off for status checks.</span>
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <input
                        type="checkbox"
                        checked={selectedStepRequiresOfficialArtifact}
                        disabled={selectedStep.stage_tag !== 'apply'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateStep(selectedStep.id, {
                              artifact_requirement: 'apply_required',
                              artifact_location: selectedStep.artifact_location && selectedStep.artifact_location !== 'none'
                                ? selectedStep.artifact_location
                                : 'headrush',
                              artifact_config: getArtifactConfig(selectedStep),
                              coach_mode: selectedStep.coach_mode || 'off',
                            });
                          } else {
                            updateStep(selectedStep.id, {
                              artifact_requirement: 'none',
                              artifact_location: 'none',
                              artifact_config: {},
                              coach_mode: 'off',
                            });
                          }
                        }}
                      />
                      Require official artifact
                    </label>
                    <select
                      className="w-full border rounded px-3 py-2 text-sm"
                      aria-label="Submission location"
                      value={selectedStep.artifact_location || 'none'}
                      onChange={(e) => updateStep(selectedStep.id, { artifact_location: e.target.value as EditorStep['artifact_location'] })}
                      disabled={!selectedStepRequiresOfficialArtifact}
                    >
                      {artifactLocationOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500">
                      {selectedStep.stage_tag === 'apply'
                        ? 'Only APPLY steps may require an official artifact.'
                        : 'Switch this step to APPLY to enable official artifact submission.'}
                    </p>
                  </div>
                </div>

                {selectedStepRequiresOfficialArtifact && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-800">Artifact prompt</label>
                      <textarea
                        className="w-full border rounded px-3 py-2 text-sm"
                        aria-label="Artifact prompt"
                        rows={3}
                        value={selectedStepArtifactPrompt}
                        onChange={(e) => updateStepArtifactConfig(selectedStep.id, (current) => ({
                          ...current,
                          prompt: e.target.value,
                        }))}
                        placeholder="What should the student submit as the official artifact?"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-800">Open in Headrush URL</label>
                      <input
                        className="w-full border rounded px-3 py-2 text-sm"
                        aria-label="Open in Headrush URL"
                        value={selectedStepArtifactExternalUrl}
                        onChange={(e) => updateStepArtifactConfig(selectedStep.id, (current) => ({
                          ...current,
                          external_url: e.target.value,
                        }))}
                        placeholder="https://..."
                        disabled={selectedStep.artifact_location !== 'headrush'}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-800">External assignment reference</label>
                      <input
                        className="w-full border rounded px-3 py-2 text-sm"
                        aria-label="External assignment reference"
                        value={selectedStepArtifactExternalAssignmentId}
                        onChange={(e) => updateStepArtifactConfig(selectedStep.id, (current) => ({
                          ...current,
                          external_assignment_id: e.target.value,
                        }))}
                        placeholder="Optional assignment id"
                        disabled={selectedStep.artifact_location !== 'headrush'}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <input
                          type="checkbox"
                          checked={selectedStep.coach_mode === 'optional'}
                          onChange={(e) => updateStep(selectedStep.id, { coach_mode: e.target.checked ? 'optional' : 'off' })}
                        />
                        Enable draft coach
                      </label>
                      <p className="text-xs text-gray-500">Students can optionally ask AI for draft feedback before they submit externally.</p>
                    </div>
                  </div>
                )}

                {selectedStep.counts_as_evidence && !legacyEvidenceCompatibilityMode && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Legacy evidence is still attached to this step. New authoring should use live response or official artifact instead.
                  </div>
                )}
              </div>
              )}

              {whyEvidenceNotice && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                  {whyEvidenceNotice}
                </div>
              )}

              {saveError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {saveError}
                </div>
              )}

              {!selectedStepIsSimulation && (
              <Tab.Group>
                <Tab.List className="flex flex-wrap gap-2 border-b border-gray-200">
                  {[
                    'Student View',
                    'Teacher Notes',
                    showLegacyEvidencePanel ? 'Legacy Evidence' : null,
                    selectedStepHasLiveResponse ? selectedStepResponseLabel : null,
                    'Preview',
                  ]
                    .filter(Boolean)
                    .map((label) => (
                      <Tab key={label as string} className={({ selected }) =>
                        `px-3 py-2 text-sm font-medium ${selected ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-gray-500'}`
                      }>
                        {label === selectedStepResponseLabel && (
                          <span className="inline-flex items-center gap-2">
                            {selectedStepResponseLabel}
                            {requiresSignalPrompt(selectedStep.signal_config?.type) &&
                            !selectedStep.signal_config?.prompt?.trim() ? (
                              <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px]">
                                Prompt required
                              </span>
                            ) : null}
                            {selectedStep.signal_config?.type === 'mcq' &&
                              (selectedStep.signal_config?.options || []).filter((option: any) => (typeof option === 'string' ? option : option?.text || '').trim()).length < 2 && (
                                <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px]">
                                  MCQ 2+
                                </span>
                              )}
                          </span>
                        )}
                        {label !== selectedStepResponseLabel && (label as string)}
                      </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className="pt-4">
                  <Tab.Panel>
                    {selectedStep.step_kind === 'quiz' ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
                        <p className="text-sm font-medium text-amber-900">Legacy quiz configuration</p>
                        <p className="text-sm text-amber-800">
                          This configuration is not delivered by the runner. Change Step kind to assessment,
                          then configure Multiple choice in the live response panel.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <button
                            className="px-3 py-2 border rounded text-sm"
                            onClick={() => applyContentUpdate([...selectedBlocks, { type: 'text', text: '' }], teacherNotes)}
                          >
                            + Text
                          </button>
                          <button
                            className="px-3 py-2 border rounded text-sm"
                            onClick={() => applyContentUpdate([...selectedBlocks, { type: 'code', code: '', language: '' }], teacherNotes)}
                          >
                            + Code
                          </button>
                          <button
                            className="px-3 py-2 border rounded text-sm"
                            onClick={() => applyContentUpdate([...selectedBlocks, { type: 'link', url: '', label: '' }], teacherNotes)}
                          >
                            + Link
                          </button>
                          <button
                            className="px-3 py-2 border border-indigo-300 rounded text-sm text-indigo-800"
                            onClick={() => applyContentUpdate([...selectedBlocks, { type: 'image', src: '', alt: '', caption: '', display: 'contain', allow_zoom: true }], teacherNotes)}
                          >
                            + Image
                          </button>
                          <button
                            className="px-3 py-2 border border-indigo-300 rounded text-sm text-indigo-800"
                            onClick={() => applyContentUpdate([...selectedBlocks, { type: 'video', src: '', poster: '', caption: '', transcript: '', allow_speed_control: true }], teacherNotes)}
                          >
                            + Video
                          </button>
                        </div>
                        <BlockEditor
                          blocks={selectedBlocks}
                          onChange={(blocks) => applyContentUpdate(blocks, teacherNotes)}
                        />
                        {selectedBlocks.length === 0 && (
                          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
                            Add a Text, Code, Link, Image, or Video block to show student content.
                          </div>
                        )}
                      </div>
                    )}
                  </Tab.Panel>
                  <Tab.Panel>
                    <textarea
                      className="w-full border rounded px-3 py-2 text-sm"
                      rows={6}
                      value={teacherNotes}
                      onChange={(e) => applyContentUpdate(selectedBlocks, e.target.value)}
                      placeholder="Teacher-only notes (not visible to students)"
                    />
                  </Tab.Panel>
                  {showLegacyEvidencePanel && (
                    <Tab.Panel>
                      <div className="space-y-3">
                        {!legacyEvidenceCompatibilityMode && selectedStep.counts_as_evidence && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-3">
                            <p>This step is still using the legacy evidence flow. You can turn it off here, but new steps should use live response or official artifact instead.</p>
                            <button
                              type="button"
                              className="px-3 py-2 rounded border border-amber-300 bg-white text-amber-900"
                              onClick={() => updateStep(selectedStep.id, {
                                counts_as_evidence: false,
                                evidence_config: {},
                              })}
                            >
                              Disable legacy evidence
                            </button>
                          </div>
                        )}
                        {legacyEvidenceCompatibilityMode && (
                          <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                            <input
                              type="checkbox"
                              checked={selectedStep.counts_as_evidence}
                              onChange={(e) => updateStep(selectedStep.id, {
                                counts_as_evidence: e.target.checked,
                                evidence_config: e.target.checked
                                  ? {
                                    type: selectedStep.evidence_config?.type || 'text',
                                    required: true,
                                    prompt: selectedStep.evidence_config?.prompt || '',
                                  }
                                  : {},
                              })}
                            />
                            Enable legacy evidence
                          </label>
                        )}
                        <select
                          className="w-full border rounded px-3 py-2 text-sm"
                          value={selectedStep.evidence_config?.type || 'text'}
                          onChange={(e) => updateStep(selectedStep.id, {
                            evidence_config: { ...selectedStep.evidence_config, type: e.target.value, required: true },
                          })}
                          disabled={!selectedStep.counts_as_evidence}
                        >
                          <option value="text">Text</option>
                          <option value="link">Link</option>
                        </select>
                        <input
                          className="w-full border rounded px-3 py-2 text-sm"
                          value={selectedStep.evidence_config?.prompt || ''}
                          onChange={(e) => updateStep(selectedStep.id, {
                            evidence_config: { ...selectedStep.evidence_config, prompt: e.target.value, required: true },
                          })}
                          placeholder="Evidence prompt"
                          disabled={!selectedStep.counts_as_evidence}
                        />
                        <p className="text-xs text-gray-500">This is legacy compatibility only. New steps should avoid the in-run evidence panel.</p>
                      </div>
                    </Tab.Panel>
                  )}
                  <div className="space-y-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-emerald-800">Support</p>
                        <p className="mt-1 text-xs text-emerald-900/80">
                          Author the explanation, model, and hints that student help and teacher AI should use.
                        </p>
                      </div>
                      <label className="flex items-center gap-2 text-sm font-medium text-emerald-950">
                        <input
                          type="checkbox"
                          checked={selectedStepSupport.enabled}
                          onChange={(e) => updateStepSupport(selectedStep.id, (currentSupport) => ({
                            ...currentSupport,
                            enabled: e.target.checked,
                          }))}
                        />
                        Enable student help
                      </label>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-wide text-emerald-800">Student model/example</label>
                        <textarea
                          aria-label="Student model/example"
                          className="min-h-[84px] w-full rounded border px-3 py-2 text-sm"
                          value={selectedStepSupport.student_model_md}
                          onChange={(e) => updateStepSupport(selectedStep.id, (currentSupport) => ({
                            ...currentSupport,
                            student_model_md: e.target.value,
                          }))}
                          placeholder="One concrete model students can anchor to."
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-wide text-emerald-800">Teacher explanation</label>
                        <textarea
                          aria-label="Teacher explanation"
                          className="min-h-[84px] w-full rounded border px-3 py-2 text-sm"
                          value={selectedStepSupport.teacher_explanation_md}
                          onChange={(e) => updateStepSupport(selectedStep.id, (currentSupport) => ({
                            ...currentSupport,
                            teacher_explanation_md: e.target.value,
                          }))}
                          placeholder="How you would explain this step out loud."
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="space-y-1">
                          <label className="text-xs uppercase tracking-wide text-emerald-800">Hint</label>
                          <textarea
                            aria-label="Hint"
                            className="min-h-[84px] w-full rounded border px-3 py-2 text-sm"
                            value={selectedStepSupport.hint_md}
                            onChange={(e) => updateStepSupport(selectedStep.id, (currentSupport) => ({
                              ...currentSupport,
                              hint_md: e.target.value,
                            }))}
                            placeholder="Short next move."
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs uppercase tracking-wide text-emerald-800">Common confusion</label>
                          <textarea
                            aria-label="Common confusion"
                            className="min-h-[84px] w-full rounded border px-3 py-2 text-sm"
                            value={selectedStepSupport.common_confusion_md}
                            onChange={(e) => updateStepSupport(selectedStep.id, (currentSupport) => ({
                              ...currentSupport,
                              common_confusion_md: e.target.value,
                            }))}
                            placeholder="What students usually misunderstand here."
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs uppercase tracking-wide text-emerald-800">Vocabulary</label>
                        <input
                          aria-label="Vocabulary"
                          className="w-full rounded border px-3 py-2 text-sm"
                          value={selectedStepSupport.vocabulary.join(', ')}
                          onChange={(e) => updateStepSupport(selectedStep.id, (currentSupport) => ({
                            ...currentSupport,
                            vocabulary: e.target.value
                              .split(',')
                              .map((item) => item.trim())
                              .filter(Boolean),
                          }))}
                          placeholder="Comma-separated terms to preserve"
                        />
                      </div>
                    </div>
                  </div>
                  {pacingTimerEditor}
                  {selectedStepHasLiveResponse && (
                    <Tab.Panel>
                      <div className="space-y-3">
                        {(() => {
                          const sourceItem = selectedStep.content?.source_item
                            ?? selectedStep.content?.sourceItem
                            ?? selectedStep.signal_config?.source_item
                            ?? selectedStep.signal_config?.sourceItem;
                          const signalType = String(selectedStep.signal_config?.signal_type || selectedStep.signal_config?.type || '')
                            .trim()
                            .toLowerCase()
                            .replace(/-/g, '_');
                          const shouldShowStructuredEditor = Boolean(sourceItem && typeof sourceItem === 'object') || structuredSignalTypes.includes(signalType);
                          if (!shouldShowStructuredEditor) return null;
                          const key = String(selectedStep.id);
                          const value = sourceItemJsonDraft[key] ?? '{}';
                          const error = sourceItemJsonErrors[key] ?? '';
                          return (
                            <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                              <p className="text-xs uppercase tracking-wide text-gray-500">Structured Source Item (JSON)</p>
                              <textarea
                                className="w-full border rounded px-3 py-2 text-sm font-mono"
                                rows={8}
                                value={value}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setSourceItemJsonDraft((prev) => ({ ...prev, [key]: raw }));
                                  try {
                                    const parsed = JSON.parse(raw || '{}');
                                    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                                      setSourceItemJsonErrors((prev) => ({ ...prev, [key]: 'JSON must be an object.' }));
                                      return;
                                    }
                                    setSourceItemJsonErrors((prev) => ({ ...prev, [key]: '' }));
                                  } catch {
                                    setSourceItemJsonErrors((prev) => ({ ...prev, [key]: 'Invalid JSON.' }));
                                  }
                                }}
                                onBlur={() => {
                                  const raw = sourceItemJsonDraft[key] ?? '{}';
                                  try {
                                    const parsed = JSON.parse(raw || '{}');
                                    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                                      setSourceItemJsonErrors((prev) => ({ ...prev, [key]: 'JSON must be an object.' }));
                                      return;
                                    }
                                    const currentSourceItem = selectedStep.content?.source_item ?? selectedStep.content?.sourceItem ?? {};
                                    if (JSON.stringify(currentSourceItem) === JSON.stringify(parsed)) {
                                      setSourceItemJsonErrors((prev) => ({ ...prev, [key]: '' }));
                                      return;
                                    }
                                    setSourceItemJsonErrors((prev) => ({ ...prev, [key]: '' }));
                                    updateStructuredSourceItem(() => parsed);
                                  } catch {
                                    setSourceItemJsonErrors((prev) => ({ ...prev, [key]: 'Invalid JSON.' }));
                                  }
                                }}
                                placeholder='{"type":"matching","question":"...","pairs":{"A":"1"}}'
                              />
                              {error ? (
                                <p className="text-xs text-red-600">{error}</p>
                              ) : (
                                <p className="text-xs text-gray-500">Edits are applied on blur when JSON is valid.</p>
                              )}
                            </div>
                          );
                        })()}
                        {(() => {
                          const sourceItemValue = selectedStep.content?.source_item
                            ?? selectedStep.content?.sourceItem
                            ?? selectedStep.signal_config?.source_item
                            ?? selectedStep.signal_config?.sourceItem;
                          const sourceItem = sourceItemValue && typeof sourceItemValue === 'object'
                            ? sourceItemValue
                            : null;
                          const signalType = String(selectedStep.signal_config?.signal_type || selectedStep.signal_config?.type || '')
                            .trim()
                            .toLowerCase()
                            .replace(/-/g, '_');
                          if (!sourceItem && (signalType === 'drag_drop_code' || signalType === 'drag_drop_fill_blanks')) {
                            const initType =
                              signalType === 'drag_drop_fill_blanks'
                                ? 'drag_drop_fill_blanks'
                                : 'drag_drop_code';
                            return (
                              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Drag Drop Code Editor</p>
                                <button
                                  className="px-3 py-2 border rounded text-sm"
                                  onClick={() => updateStructuredSourceItem(() => ({
                                    type: initType,
                                    question: '',
                                    target: '',
                                    tokens: [],
                                    answer_code: '',
                                  }))}
                                >
                                  Initialize drag-drop source item
                                </button>
                              </div>
                            );
                          }
                          const sourceType = String(sourceItem?.type || '').trim().toLowerCase();
                          if (sourceType === 'matching') {
                            const matchingKey = String(selectedStep.id);
                            const pairRows = matchingDraftRows[matchingKey] || [{ left: '', right: '' }];
                            const applyMatchingRows = (rows: Array<{ left: string; right: string }>) => {
                              setMatchingDraftRows((prev) => ({ ...prev, [matchingKey]: rows }));
                              const pairs = matchingRowsToPairs(rows);
                              updateStructuredSourceItem((current) => ({ ...current, type: 'matching', pairs }));
                            };
                            return (
                              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Matching Editor</p>
                                <input
                                  className="w-full border rounded px-3 py-2 text-sm"
                                  value={String(sourceItem?.question || '')}
                                  onChange={(e) => {
                                    const question = e.target.value;
                                    updateStructuredSourceItem((current) => ({ ...current, type: 'matching', question }));
                                  }}
                                  placeholder="Matching question"
                                />
                                {pairRows.map((row, idx) => (
                                  <div key={`pair-${idx}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                                    <input
                                      className="border rounded px-3 py-2 text-sm"
                                      value={String(row.left || '')}
                                      placeholder="Left"
                                      onChange={(e) => {
                                        const nextRows = [...pairRows];
                                        nextRows[idx] = { ...row, left: e.target.value };
                                        applyMatchingRows(nextRows);
                                      }}
                                    />
                                    <input
                                      className="border rounded px-3 py-2 text-sm"
                                      value={String(row.right || '')}
                                      placeholder="Right"
                                      onChange={(e) => {
                                        const nextRows = [...pairRows];
                                        nextRows[idx] = { ...row, right: e.target.value };
                                        applyMatchingRows(nextRows);
                                      }}
                                    />
                                    <button
                                      className="px-2 py-1 border rounded text-xs"
                                      onClick={() => {
                                        const nextRows = pairRows.filter((_, rowIdx) => rowIdx !== idx);
                                        applyMatchingRows(nextRows.length > 0 ? nextRows : [{ left: '', right: '' }]);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ))}
                                <button
                                  className="px-3 py-2 border rounded text-sm"
                                  onClick={() => {
                                    applyMatchingRows([...pairRows, { left: '', right: '' }]);
                                  }}
                                >
                                  Add row
                                </button>
                              </div>
                            );
                          }
                          if (sourceType === 'ordering') {
                            const items = Array.isArray(sourceItem?.items) ? sourceItem.items.map((item: any) => String(item || '')) : [];
                            const answer = Array.isArray(sourceItem?.answer) ? sourceItem.answer.map((item: any) => String(item || '')) : [];
                            return (
                              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Ordering Editor</p>
                                <input
                                  className="w-full border rounded px-3 py-2 text-sm"
                                  value={String(sourceItem?.question || '')}
                                  onChange={(e) => {
                                    const question = e.target.value;
                                    updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', question }));
                                  }}
                                  placeholder="Ordering question"
                                />
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div className="space-y-2">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Items</p>
                                    {items.map((item: string, idx: number) => (
                                      <div key={`item-${idx}`} className="flex items-center gap-1">
                                        <input
                                          className="flex-1 border rounded px-3 py-2 text-sm"
                                          value={item}
                                          onChange={(e) => {
                                            const nextItems = [...items];
                                            nextItems[idx] = e.target.value;
                                            updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items: nextItems, answer }));
                                          }}
                                        />
                                        <button className="px-2 py-1 border rounded text-xs" onClick={() => {
                                          if (idx === 0) return;
                                          const nextItems = [...items];
                                          [nextItems[idx - 1], nextItems[idx]] = [nextItems[idx], nextItems[idx - 1]];
                                          updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items: nextItems, answer }));
                                        }}>↑</button>
                                        <button className="px-2 py-1 border rounded text-xs" onClick={() => {
                                          if (idx === items.length - 1) return;
                                          const nextItems = [...items];
                                          [nextItems[idx + 1], nextItems[idx]] = [nextItems[idx], nextItems[idx + 1]];
                                          updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items: nextItems, answer }));
                                        }}>↓</button>
                                        <button className="px-2 py-1 border rounded text-xs" onClick={() => {
                                          const nextItems = items.filter((_: string, i: number) => i !== idx);
                                          updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items: nextItems, answer }));
                                        }}>✕</button>
                                      </div>
                                    ))}
                                    <button className="px-3 py-2 border rounded text-sm" onClick={() => {
                                      updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items: [...items, ''], answer }));
                                    }}>Add item</button>
                                  </div>
                                  <div className="space-y-2">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Answer</p>
                                    {answer.map((item: string, idx: number) => (
                                      <div key={`answer-${idx}`} className="flex items-center gap-1">
                                        <input
                                          className="flex-1 border rounded px-3 py-2 text-sm"
                                          value={item}
                                          onChange={(e) => {
                                            const nextAnswer = [...answer];
                                            nextAnswer[idx] = e.target.value;
                                            updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items, answer: nextAnswer }));
                                          }}
                                        />
                                        <button className="px-2 py-1 border rounded text-xs" onClick={() => {
                                          if (idx === 0) return;
                                          const nextAnswer = [...answer];
                                          [nextAnswer[idx - 1], nextAnswer[idx]] = [nextAnswer[idx], nextAnswer[idx - 1]];
                                          updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items, answer: nextAnswer }));
                                        }}>↑</button>
                                        <button className="px-2 py-1 border rounded text-xs" onClick={() => {
                                          if (idx === answer.length - 1) return;
                                          const nextAnswer = [...answer];
                                          [nextAnswer[idx + 1], nextAnswer[idx]] = [nextAnswer[idx], nextAnswer[idx + 1]];
                                          updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items, answer: nextAnswer }));
                                        }}>↓</button>
                                        <button className="px-2 py-1 border rounded text-xs" onClick={() => {
                                          const nextAnswer = answer.filter((_: string, i: number) => i !== idx);
                                          updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items, answer: nextAnswer }));
                                        }}>✕</button>
                                      </div>
                                    ))}
                                    <button className="px-3 py-2 border rounded text-sm" onClick={() => {
                                      updateStructuredSourceItem((current) => ({ ...current, type: 'ordering', items, answer: [...answer, ''] }));
                                    }}>Add answer item</button>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (sourceType === 'drag_drop_code' || sourceType === 'drag_drop_fill_blanks') {
                            const dragType =
                              sourceType === 'drag_drop_fill_blanks'
                                ? 'drag_drop_fill_blanks'
                                : 'drag_drop_code';
                            const tokenBank = Array.isArray(sourceItem?.token_bank)
                              ? sourceItem.token_bank.map((item: any) => String(item || ''))
                              : (Array.isArray(sourceItem?.tokens)
                                ? sourceItem.tokens.map((item: any) => String(item || ''))
                                : (Array.isArray(sourceItem?.options) ? sourceItem.options.map((item: any) => String(item || '')) : []));
                            const promptLines = Array.isArray(sourceItem?.prompt_lines)
                              ? sourceItem.prompt_lines.map((line: any) => String(line || ''))
                              : String(sourceItem?.target || sourceItem?.code || sourceItem?.template || '')
                                .split('\n')
                                .map((line) => line.replace(/\r/g, ''))
                                .filter((line) => line.trim().length > 0);
                            const targetTemplate = String(sourceItem?.target || sourceItem?.code || sourceItem?.template || '');
                            const targetBlankCount = (targetTemplate.match(/____/g) || []).length;
                            const blanksRaw = Number(sourceItem?.blanks);
                            const configuredBlankCount = Number.isInteger(blanksRaw) && blanksRaw > 0 ? blanksRaw : 0;
                            const blankCount = Math.max(targetBlankCount, configuredBlankCount);
                            const rawAnswerTokens = Array.isArray(sourceItem?.answer_tokens)
                              ? sourceItem.answer_tokens
                              : [];
                            const answerTokens = Array.from({ length: blankCount }, (_, idx) => {
                              const value = rawAnswerTokens[idx];
                              if (value === null || value === undefined) return null;
                              const text = String(value).trim();
                              return text.length > 0 ? text : null;
                            });
                            const showAnswerTokensEditor =
                              dragType === 'drag_drop_code' ||
                              dragType === 'drag_drop_fill_blanks' ||
                              signalType === 'drag_drop_code' ||
                              signalType === 'drag_drop_fill_blanks';
                            return (
                              <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Drag Drop Code Editor</p>
                                <input
                                  className="w-full border rounded px-3 py-2 text-sm"
                                  value={String(sourceItem?.question || '')}
                                  onChange={(e) => {
                                    const question = e.target.value;
                                    updateStructuredSourceItem((current) => ({ ...current, type: dragType, question }));
                                  }}
                                  placeholder="Question"
                                />
                                <textarea
                                  className="w-full border rounded px-3 py-2 text-sm font-mono"
                                  rows={3}
                                  value={String(sourceItem?.target || sourceItem?.code || sourceItem?.template || '')}
                                  onChange={(e) => {
                                    const target = e.target.value;
                                    updateStructuredSourceItem((current) => ({ ...current, type: dragType, target }));
                                  }}
                                  placeholder='Target template (use ____ for blanks)'
                                />
                                <textarea
                                  className="w-full border rounded px-3 py-2 text-sm font-mono"
                                  rows={3}
                                  value={String(sourceItem?.answer_code || '')}
                                  onChange={(e) => {
                                    const answerCode = e.target.value;
                                    updateStructuredSourceItem((current) => ({ ...current, type: dragType, answer_code: answerCode }));
                                  }}
                                  placeholder="Answer code (teacher only)"
                                />
                                <div className="space-y-2">
                                  <p className="text-xs uppercase tracking-wide text-gray-500">Tokens</p>
                                  {tokenBank.map((token: string, idx: number) => (
                                    <div key={`drag-token-${idx}`} className="flex items-center gap-2">
                                      <input
                                        className="flex-1 border rounded px-3 py-2 text-sm"
                                        value={token}
                                        onChange={(e) => {
                                          const nextTokens = [...tokenBank];
                                          nextTokens[idx] = e.target.value;
                                          updateStructuredSourceItem((current) => ({
                                            ...current,
                                            type: dragType,
                                            token_bank: nextTokens,
                                            tokens: nextTokens,
                                          }));
                                        }}
                                      />
                                      <button
                                        className="px-2 py-1 border rounded text-xs"
                                        onClick={() => {
                                          const nextTokens = tokenBank.filter((_: string, tokenIdx: number) => tokenIdx !== idx);
                                          updateStructuredSourceItem((current) => ({
                                            ...current,
                                            type: dragType,
                                            token_bank: nextTokens,
                                            tokens: nextTokens,
                                          }));
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    className="px-3 py-2 border rounded text-sm"
                                    onClick={() => {
                                      const nextTokens = [...tokenBank, ''];
                                      updateStructuredSourceItem((current) => ({
                                        ...current,
                                        type: dragType,
                                        token_bank: nextTokens,
                                        tokens: nextTokens,
                                      }));
                                    }}
                                  >
                                    Add token
                                  </button>
                                </div>
                                {showAnswerTokensEditor && (
                                  <div className="space-y-2">
                                    <p className="text-xs uppercase tracking-wide text-gray-500">Answers</p>
                                    <p className="text-xs text-gray-600">
                                      Blanks detected: <span className="font-semibold">{blankCount}</span>
                                      {' • '}
                                      Answer tokens filled: <span className="font-semibold">{answerTokens.filter((item) => Boolean(item)).length}</span>
                                      {blankCount > 0 && answerTokens.filter((item) => Boolean(item)).length < blankCount ? (
                                        <span className="text-amber-700"> (incomplete: reveal grading will stay unavailable)</span>
                                      ) : null}
                                    </p>
                                    {promptLines.length === 0 ? null : (
                                      <div className="space-y-1">
                                        {promptLines.map((line: string, idx: number) => (
                                          <div key={`answer-line-preview-${idx}`} className="rounded border bg-gray-50 px-2 py-1 text-xs font-mono text-gray-700 whitespace-pre-wrap">
                                            {line}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {blankCount <= 0 ? (
                                      <p className="text-xs text-gray-500">No blanks found in target yet.</p>
                                    ) : (
                                      Array.from({ length: blankCount }).map((_, idx: number) => (
                                        <div key={`answer-token-${idx}`} className="grid gap-2 md:grid-cols-[1fr_220px] md:items-center">
                                          <div className="rounded border bg-gray-50 px-2 py-1 text-xs text-gray-700">
                                            Blank {idx + 1}
                                          </div>
                                          <select
                                            className="w-full border rounded px-3 py-2 text-sm"
                                            value={answerTokens[idx] || ''}
                                            onChange={(e) => {
                                              const nextAnswerTokens = Array.from(
                                                { length: blankCount },
                                                (_, answerIdx) => answerTokens[answerIdx] ?? null,
                                              );
                                              const nextValue = String(e.target.value || '').trim();
                                              nextAnswerTokens[idx] = nextValue ? nextValue : null;
                                              updateStructuredSourceItem((current) => ({
                                                ...current,
                                                type: dragType,
                                                answer_tokens: nextAnswerTokens,
                                              }));
                                            }}
                                          >
                                            <option value="">(blank)</option>
                                            {tokenBank.map((token: string, tokenIdx: number) => (
                                              <option key={`answer-option-${tokenIdx}-${token}`} value={token}>
                                                {token}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {requiresSignalPrompt(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type) &&
                        !selectedStep.signal_config?.prompt?.trim() ? (
                          <span className="inline-flex rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs">
                            Prompt required
                          </span>
                        ) : null}
                        {normalizeSignalType(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type) === 'mcq' &&
                          (selectedStep.signal_config?.options || []).filter((option: any) => (typeof option === 'string' ? option : option?.text || '').trim()).length < 2 && (
                            <span className="inline-flex rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs">
                              MCQ needs 2+ options
                            </span>
                          )}
                        <select
                          className="w-full border rounded px-3 py-2 text-sm"
                          value={normalizeSignalType(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type || 'text')}
                          onChange={(e) => {
                            const type = normalizeSignalType(e.target.value);
                            const existingReasoningGate = selectedStep.signal_config?.reasoning_gate || {};
                            const existingModeRaw = typeof existingReasoningGate.mode === 'string'
                              ? existingReasoningGate.mode.trim().toLowerCase()
                              : '';
                            const mode = (existingModeRaw === 'required' || existingModeRaw === 'optional' || existingModeRaw === 'off')
                              ? existingModeRaw
                              : 'off';
                            const minCharsRaw = Number(existingReasoningGate.min_chars);
                            const minChars = Number.isInteger(minCharsRaw) && minCharsRaw >= 0 ? minCharsRaw : 15;
                            const existingConfidenceRequired = typeof selectedStep.signal_config?.confidence_required === 'boolean'
                              ? selectedStep.signal_config.confidence_required
                              : undefined;
                            const baseConfig = {
                              reason_required: mode === 'required',
                              confidence_required: type === 'guided_sequence'
                                ? false
                                : (existingConfidenceRequired ?? false),
                              reasoning_gate: { mode, min_chars: minChars },
                            };
                            const nextPatch: Partial<EditorStep> = {
                              signal_config: type === 'mcq'
                                ? { type, prompt: '', options: ['A', 'B'], ...baseConfig }
                                : (type === 'guided_sequence'
                                  ? {
                                      type,
                                      title: 'Explain Readiness — System Spec',
                                      submit_label: 'Submit Explain Readiness',
                                      cards: buildDefaultGuidedSequenceCards(),
                                      ...baseConfig,
                                    }
                                  : { type, prompt: '', ...baseConfig }),
                            };
                            if (type === 'ordering' || type === 'matching') {
                              const currentContent = selectedStep.content && typeof selectedStep.content === 'object'
                                ? selectedStep.content
                                : {};
                              const sourceItem = currentContent.source_item && typeof currentContent.source_item === 'object'
                                ? currentContent.source_item
                                : (currentContent.sourceItem && typeof currentContent.sourceItem === 'object'
                                  ? currentContent.sourceItem
                                  : null);
                              const nextSourceItem = sourceItem && typeof sourceItem === 'object'
                                ? { ...sourceItem, type }
                                : (
                                  type === 'matching'
                                    ? { type: 'matching', question: '', pairs: {} }
                                    : { type: 'ordering', question: '', items: [], answer: [] }
                                );
                              const restContent = { ...(currentContent as Record<string, any>) };
                              delete restContent.sourceItem;
                              nextPatch.content = normalizeStructuredPromptContent({
                                ...restContent,
                                source_item: nextSourceItem,
                              });
                            }
                            updateStep(selectedStep.id, nextPatch);
                          }}
                        >
                          <option value="text">Text</option>
                          <option value="mcq">Multiple choice</option>
                          <option value="guided_sequence">Guided sequence</option>
                          <option value="matching">Matching</option>
                          <option value="ordering">Ordering</option>
                          <option value="drag_drop_code">Drag &amp; Drop Code</option>
                          <option value="drag_drop_fill_blanks">Drag &amp; Drop Fill Blanks</option>
                        </select>
                        {normalizeSignalType(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type) === 'guided_sequence' ? (
                          <div className="space-y-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
                            <input
                              className="w-full border rounded px-3 py-2 text-sm"
                              value={selectedStep.signal_config?.title || ''}
                              onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => ({
                                ...currentSignalConfig,
                                title: e.target.value,
                              }))}
                              placeholder="Guided sequence title"
                            />
                            <input
                              className="w-full border rounded px-3 py-2 text-sm"
                              value={selectedStep.signal_config?.submit_label || ''}
                              onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => ({
                                ...currentSignalConfig,
                                submit_label: e.target.value,
                              }))}
                              placeholder="Submit button label"
                            />
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <p className="text-xs uppercase tracking-wide text-gray-500">Guided cards</p>
                                <button
                                  type="button"
                                  className="px-2 py-1 rounded border border-gray-300 bg-white text-xs"
                                  onClick={() => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                    const currentCards = Array.isArray(currentSignalConfig.cards)
                                      ? [...currentSignalConfig.cards]
                                      : [];
                                    const nextIndex = currentCards.length + 1;
                                    currentCards.push({
                                      key: `card_${nextIndex}`,
                                      prompt: `Prompt ${nextIndex}`,
                                      input_type: 'text',
                                      required: true,
                                      placeholder: '',
                                      validation: { min_words: 3, max_chars: 220 },
                                    });
                                    return {
                                      ...currentSignalConfig,
                                      cards: currentCards,
                                    };
                                  })}
                                >
                                  Add card
                                </button>
                              </div>
                              {(Array.isArray(selectedStep.signal_config?.cards) ? selectedStep.signal_config.cards : []).map((card: any, cardIndex: number) => (
                                <div key={`guided-card-${cardIndex}`} className="rounded border border-blue-100 bg-white p-3 space-y-2">
                                  <div className="grid gap-2 sm:grid-cols-2">
                                    <input
                                      className="border rounded px-2 py-1 text-xs"
                                      value={String(card?.key || '')}
                                      onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                        const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                        const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                          ? { ...currentCards[cardIndex] }
                                          : {};
                                        currentCards[cardIndex] = { ...currentCard, key: e.target.value };
                                        return { ...currentSignalConfig, cards: currentCards };
                                      })}
                                      placeholder="Card key"
                                    />
                                    <select
                                      className="border rounded px-2 py-1 text-xs"
                                      value={String(card?.input_type || 'text')}
                                      onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                        const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                        const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                          ? { ...currentCards[cardIndex] }
                                          : {};
                                        const nextInputType = e.target.value === 'textarea' || e.target.value === 'select'
                                          ? e.target.value
                                          : 'text';
                                        currentCards[cardIndex] = { ...currentCard, input_type: nextInputType };
                                        return { ...currentSignalConfig, cards: currentCards };
                                      })}
                                    >
                                      <option value="text">Text</option>
                                      <option value="textarea">Textarea</option>
                                      <option value="select">Choice list</option>
                                    </select>
                                  </div>
                                  <input
                                    className="w-full border rounded px-2 py-1 text-xs"
                                    value={String(card?.prompt || '')}
                                    onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                      const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                      const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                        ? { ...currentCards[cardIndex] }
                                        : {};
                                      currentCards[cardIndex] = { ...currentCard, prompt: e.target.value };
                                      return { ...currentSignalConfig, cards: currentCards };
                                    })}
                                    placeholder="Prompt"
                                  />
                                  <input
                                    className="w-full border rounded px-2 py-1 text-xs"
                                    value={String(card?.placeholder || '')}
                                    onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                      const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                      const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                        ? { ...currentCards[cardIndex] }
                                        : {};
                                      currentCards[cardIndex] = { ...currentCard, placeholder: e.target.value };
                                      return { ...currentSignalConfig, cards: currentCards };
                                    })}
                                    placeholder="Placeholder"
                                  />
                                  {String(card?.input_type || 'text') === 'select' ? (
                                    <textarea
                                      className="w-full border rounded px-2 py-1 text-xs"
                                      rows={4}
                                      value={(Array.isArray(card?.options) ? card.options : []).map((option: any) => {
                                        if (option && typeof option === 'object') {
                                          return `${String(option.value ?? '').trim()} | ${String(option.label ?? option.value ?? '').trim()}`;
                                        }
                                        return String(option ?? '').trim();
                                      }).filter(Boolean).join('\n')}
                                      onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                        const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                        const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                          ? { ...currentCards[cardIndex] }
                                          : {};
                                        const options = e.target.value.split('\n').map((line) => {
                                          const [rawValue, ...labelParts] = line.split('|');
                                          const value = rawValue.trim();
                                          const label = labelParts.join('|').trim() || value;
                                          return value ? { value, label } : null;
                                        }).filter(Boolean);
                                        currentCards[cardIndex] = { ...currentCard, options };
                                        return { ...currentSignalConfig, cards: currentCards };
                                      })}
                                      placeholder={'mostly_clear | Mostly clear\nnot_clear_yet | Not clear yet'}
                                    />
                                  ) : null}
                                  <input
                                    className="w-full border rounded px-2 py-1 text-xs"
                                    value={String(card?.template || '')}
                                    onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                      const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                      const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                        ? { ...currentCards[cardIndex] }
                                        : {};
                                      currentCards[cardIndex] = { ...currentCard, template: e.target.value };
                                      return { ...currentSignalConfig, cards: currentCards };
                                    })}
                                    placeholder="Template (optional)"
                                  />
                                  <div className="grid gap-2 sm:grid-cols-4">
                                    <input
                                      className="border rounded px-2 py-1 text-xs"
                                      type="number"
                                      min={1}
                                      value={optionalPositiveIntegerDisplayValue(card?.validation?.min_words)}
                                      onChange={(e) => updateStepSignalConfig(
                                        selectedStep.id,
                                        (currentSignalConfig) => updateGuidedSequenceCardValidationNumber(currentSignalConfig, cardIndex, 'min_words', e.target.value),
                                      )}
                                      placeholder="Min words (optional)"
                                    />
                                    <input
                                      className="border rounded px-2 py-1 text-xs"
                                      type="number"
                                      min={1}
                                      value={optionalPositiveIntegerDisplayValue(card?.validation?.min_chars)}
                                      onChange={(e) => updateStepSignalConfig(
                                        selectedStep.id,
                                        (currentSignalConfig) => updateGuidedSequenceCardValidationNumber(currentSignalConfig, cardIndex, 'min_chars', e.target.value),
                                      )}
                                      placeholder="Min chars (optional)"
                                    />
                                    <input
                                      className="border rounded px-2 py-1 text-xs"
                                      type="number"
                                      min={1}
                                      value={optionalPositiveIntegerDisplayValue(card?.validation?.max_chars)}
                                      onChange={(e) => updateStepSignalConfig(
                                        selectedStep.id,
                                        (currentSignalConfig) => updateGuidedSequenceCardValidationNumber(currentSignalConfig, cardIndex, 'max_chars', e.target.value),
                                      )}
                                      placeholder="Max chars (optional)"
                                    />
                                    <input
                                      className="border rounded px-2 py-1 text-xs"
                                      value={String(card?.validation?.must_start_with || '')}
                                      onChange={(e) => updateStepSignalConfig(
                                        selectedStep.id,
                                        (currentSignalConfig) => updateGuidedSequenceCardValidationText(currentSignalConfig, cardIndex, 'must_start_with', e.target.value),
                                      )}
                                      placeholder="Must start with"
                                    />
                                  </div>
                                  <div className="flex items-center justify-between gap-3">
                                    <label className="flex items-center gap-2 text-xs text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(card?.required)}
                                        onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                          const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                          const currentCard = currentCards[cardIndex] && typeof currentCards[cardIndex] === 'object'
                                            ? { ...currentCards[cardIndex] }
                                            : {};
                                          currentCards[cardIndex] = { ...currentCard, required: e.target.checked };
                                          return { ...currentSignalConfig, cards: currentCards };
                                        })}
                                      />
                                      Required
                                    </label>
                                    <button
                                      type="button"
                                      className="text-xs text-red-600"
                                      onClick={() => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => {
                                        const currentCards = Array.isArray(currentSignalConfig.cards) ? [...currentSignalConfig.cards] : [];
                                        const nextCards = currentCards.filter((_: any, idx: number) => idx !== cardIndex);
                                        return { ...currentSignalConfig, cards: nextCards };
                                      })}
                                    >
                                      Remove card
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <input
                            className="w-full border rounded px-3 py-2 text-sm"
                            value={selectedStep.signal_config?.prompt || ''}
                            onChange={(e) => updateStep(selectedStep.id, {
                              signal_config: { ...selectedStep.signal_config, prompt: e.target.value },
                            })}
                            placeholder="Signal prompt"
                          />
                        )}
                        {normalizeSignalType(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type) !== 'guided_sequence' && (
                          <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-800">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              aria-label="Require confidence rating"
                              checked={selectedStep.signal_config?.confidence_required === true}
                              onChange={(e) => updateStepSignalConfig(selectedStep.id, (currentSignalConfig) => ({
                                ...currentSignalConfig,
                                confidence_required: e.target.checked,
                              }))}
                            />
                            <span>
                              <span className="block font-medium">Require confidence rating</span>
                              <span className="block text-xs text-gray-500">
                                Turn this off for status checks or routing questions where a confidence score is not useful.
                              </span>
                            </span>
                          </label>
                        )}
                        <div className="space-y-2 pt-2">
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            Reveal mode
                          </p>
                          <div className="flex items-center gap-4 text-sm">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`reveal-mode-${selectedStep.id}`}
                                value="auto"
                                checked={
                                  (selectedStep.signal_config?.reveal_mode ?? 'auto') === 'auto'
                                }
                                onChange={() =>
                                  updateStep(selectedStep.id, {
                                    signal_config: {
                                      ...selectedStep.signal_config,
                                      reveal_mode: 'auto',
                                    },
                                  })
                                }
                              />
                              Auto (immediate feedback)
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`reveal-mode-${selectedStep.id}`}
                                value="teacher"
                                checked={
                                  (selectedStep.signal_config?.reveal_mode ?? 'auto') === 'teacher'
                                }
                                onChange={() =>
                                  updateStep(selectedStep.id, {
                                    signal_config: {
                                      ...selectedStep.signal_config,
                                      reveal_mode: 'teacher',
                                    },
                                  })
                                }
                              />
                              Teacher (reveal when ready)
                            </label>
                          </div>
                        </div>
                        {(() => {
                          const currentSignalConfig = (
                            selectedStep.signal_config && typeof selectedStep.signal_config === 'object'
                          ) ? selectedStep.signal_config : {};
                          const explanationSupportRaw = (
                            currentSignalConfig.explanation_support && typeof currentSignalConfig.explanation_support === 'object'
                          ) ? currentSignalConfig.explanation_support : {};
                          const explanationSupportEnabled = explanationSupportRaw.enabled !== false;
                          const previousAnswersRaw = (
                            currentSignalConfig.previous_answers && typeof currentSignalConfig.previous_answers === 'object'
                          ) ? currentSignalConfig.previous_answers : (
                            currentSignalConfig.previousAnswers && typeof currentSignalConfig.previousAnswers === 'object'
                              ? currentSignalConfig.previousAnswers
                              : {}
                          );
                          const modeRaw = String(previousAnswersRaw.mode || '').trim().toLowerCase();
                          const mode = modeRaw === 'on' || modeRaw === 'optional' || modeRaw === 'off'
                            ? modeRaw
                            : (Object.keys(previousAnswersRaw).length > 0 ? 'optional' : 'off');
                          const sourceStepIds: number[] = Array.isArray(previousAnswersRaw.source_step_ids)
                            ? previousAnswersRaw.source_step_ids
                              .map((value: any) => Number(value))
                              .filter((value: number) => Number.isInteger(value) && value > 0)
                            : [];
                          const selectedSourceStepIds: number[] = Array.from(new Set<number>(sourceStepIds));
                          const itemsRaw = Array.isArray(previousAnswersRaw.items) ? previousAnswersRaw.items : [];
                          const labelByStepId = itemsRaw.reduce((acc: Record<number, string>, item: any) => {
                            const stepId = Number(item?.step_id);
                            if (!Number.isInteger(stepId) || stepId <= 0) return acc;
                            const label = String(item?.label || '').trim();
                            if (label) acc[stepId] = label;
                            return acc;
                          }, {});
                          const helperTitle = typeof previousAnswersRaw.title === 'string' ? previousAnswersRaw.title : '';
                          const allowInsert = Boolean(previousAnswersRaw.allow_insert === true);
                          const candidateIds = new Set<number>(previousAnswersSourceCandidates.map((item) => item.id));
                          const hasInvalidSource = selectedSourceStepIds.some((stepId) => !candidateIds.has(stepId));

                          return (
                            <div className="space-y-3">
                              <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                                <label className="flex items-start gap-3 text-sm text-indigo-950">
                                  <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={explanationSupportEnabled}
                                    onChange={(e) => {
                                      const nextChecked = e.target.checked;
                                      updateStepSignalConfig(selectedStep.id, (existingSignalConfig) => {
                                        const nextSignalConfig = { ...existingSignalConfig };
                                        if (nextChecked) {
                                          delete nextSignalConfig.explanation_support;
                                        } else {
                                          nextSignalConfig.explanation_support = { enabled: false };
                                        }
                                        return nextSignalConfig;
                                      });
                                    }}
                                  />
                                  <span>
                                    <span className="block font-semibold">Allow missed-answer AI explanation in non-live replay</span>
                                    <span className="mt-1 block text-xs text-indigo-900/80">
                                      Students can request a saved step-by-step explanation after they miss this step in an async replay assignment.
                                    </span>
                                  </span>
                                </label>
                              </div>
                              <div className="space-y-3 rounded-lg border border-cyan-100 bg-cyan-50 p-3">
                                <p className="text-xs uppercase tracking-wide text-cyan-800">Previous Answers Helper</p>
                                <select
                                  className="w-full border rounded px-3 py-2 text-sm"
                                  value={mode}
                                  onChange={(e) => {
                                    const nextMode = String(e.target.value || 'off').trim().toLowerCase();
                                    updateStepSignalConfig(selectedStep.id, (existingSignalConfig) => {
                                      const existingPrev = (
                                        existingSignalConfig.previous_answers && typeof existingSignalConfig.previous_answers === 'object'
                                      ) ? existingSignalConfig.previous_answers : {};
                                      const existingSourceIds = Array.isArray(existingPrev.source_step_ids)
                                        ? existingPrev.source_step_ids
                                          .map((value: any) => Number(value))
                                          .filter((value: number) => Number.isInteger(value) && value > 0)
                                        : [];
                                      return {
                                        ...existingSignalConfig,
                                        previous_answers: {
                                          ...existingPrev,
                                          mode: nextMode === 'on' || nextMode === 'optional' || nextMode === 'off' ? nextMode : 'off',
                                          source_step_ids: Array.from(new Set(existingSourceIds)),
                                        },
                                      };
                                    });
                                  }}
                                >
                                  <option value="off">Off</option>
                                  <option value="optional">Optional</option>
                                  <option value="on">On</option>
                                </select>
                                {mode !== 'off' && (
                                  <>
                                    <input
                                      className="w-full border rounded px-3 py-2 text-sm"
                                      value={helperTitle}
                                      onChange={(e) => {
                                        const nextValue = e.target.value;
                                        updateStepSignalConfig(selectedStep.id, (existingSignalConfig) => {
                                          const existingPrev = (
                                            existingSignalConfig.previous_answers && typeof existingSignalConfig.previous_answers === 'object'
                                          ) ? existingSignalConfig.previous_answers : {};
                                          return {
                                            ...existingSignalConfig,
                                            previous_answers: {
                                              ...existingPrev,
                                              mode,
                                              title: nextValue,
                                              source_step_ids: selectedSourceStepIds,
                                            },
                                          };
                                        });
                                      }}
                                      placeholder="Helper title (optional)"
                                    />
                                    <label className="flex items-center gap-2 text-sm text-cyan-900">
                                      <input
                                        type="checkbox"
                                        checked={allowInsert}
                                        onChange={(e) => {
                                          const nextChecked = e.target.checked;
                                          updateStepSignalConfig(selectedStep.id, (existingSignalConfig) => {
                                            const existingPrev = (
                                              existingSignalConfig.previous_answers && typeof existingSignalConfig.previous_answers === 'object'
                                            ) ? existingSignalConfig.previous_answers : {};
                                            return {
                                              ...existingSignalConfig,
                                              previous_answers: {
                                                ...existingPrev,
                                                mode,
                                                allow_insert: nextChecked,
                                                source_step_ids: selectedSourceStepIds,
                                              },
                                            };
                                          });
                                        }}
                                      />
                                      Allow one-click insert in student view
                                    </label>
                                    <div className="space-y-2">
                                      <p className="text-xs uppercase tracking-wide text-gray-500">Source steps</p>
                                      {previousAnswersSourceCandidates.length === 0 ? (
                                        <p className="text-xs text-gray-600">No earlier saved steps are available yet.</p>
                                      ) : (
                                        previousAnswersSourceCandidates.map((candidate) => {
                                          const checked = selectedSourceStepIds.includes(candidate.id);
                                          return (
                                            <label key={`previous-source-${candidate.id}`} className="flex items-center gap-2 text-sm text-gray-800">
                                              <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                  const isChecked = e.target.checked;
                                                  updateStepSignalConfig(selectedStep.id, (existingSignalConfig) => {
                                                    const existingPrev = (
                                                      existingSignalConfig.previous_answers && typeof existingSignalConfig.previous_answers === 'object'
                                                    ) ? existingSignalConfig.previous_answers : {};
                                                    const existingIds = Array.isArray(existingPrev.source_step_ids)
                                                      ? existingPrev.source_step_ids
                                                        .map((value: any) => Number(value))
                                                        .filter((value: number) => Number.isInteger(value) && value > 0)
                                                      : [];
                                                    const idSet = new Set<number>(existingIds);
                                                    if (isChecked) idSet.add(candidate.id);
                                                    else idSet.delete(candidate.id);
                                                    const nextSourceIds: number[] = Array.from(idSet).filter((stepId) => candidateIds.has(stepId));
                                                    const existingItems = Array.isArray(existingPrev.items) ? existingPrev.items : [];
                                                    const nextItems = nextSourceIds.map((stepId) => {
                                                      const existingItem = existingItems.find((item: any) => Number(item?.step_id) === stepId);
                                                      const existingLabel = String(existingItem?.label || '').trim();
                                                      return existingLabel ? { step_id: stepId, label: existingLabel } : { step_id: stepId };
                                                    });
                                                    return {
                                                      ...existingSignalConfig,
                                                      previous_answers: {
                                                        ...existingPrev,
                                                        mode,
                                                        source_step_ids: nextSourceIds,
                                                        items: nextItems,
                                                      },
                                                    };
                                                  });
                                                }}
                                              />
                                              <span>{candidate.title}</span>
                                              <span className="text-xs text-gray-500">#{candidate.order}</span>
                                            </label>
                                          );
                                        })
                                      )}
                                    </div>
                                    {selectedSourceStepIds.length > 0 && (
                                      <div className="space-y-2">
                                        <p className="text-xs uppercase tracking-wide text-gray-500">Display labels</p>
                                        {selectedSourceStepIds.map((stepId) => {
                                          const stepMeta = previousAnswersSourceCandidates.find((candidate) => candidate.id === stepId);
                                          const defaultLabel = stepMeta?.title || `Step ${stepId}`;
                                          return (
                                            <div key={`previous-label-${stepId}`} className="grid gap-2 md:grid-cols-[180px_1fr] md:items-center">
                                              <p className="text-xs text-gray-600">{defaultLabel}</p>
                                              <input
                                                className="w-full border rounded px-3 py-2 text-sm"
                                                value={labelByStepId[stepId] || ''}
                                                onChange={(e) => {
                                                  const nextLabel = e.target.value;
                                                  updateStepSignalConfig(selectedStep.id, (existingSignalConfig) => {
                                                    const existingPrev = (
                                                      existingSignalConfig.previous_answers && typeof existingSignalConfig.previous_answers === 'object'
                                                    ) ? existingSignalConfig.previous_answers : {};
                                                    const existingIds = Array.isArray(existingPrev.source_step_ids)
                                                      ? existingPrev.source_step_ids
                                                        .map((value: any) => Number(value))
                                                        .filter((value: number) => Number.isInteger(value) && value > 0)
                                                      : [];
                                                    const existingItems = Array.isArray(existingPrev.items) ? existingPrev.items : [];
                                                    const nextItems = existingIds.map((id: number) => {
                                                      const existingItem = existingItems.find((item: any) => Number(item?.step_id) === id);
                                                      const existingLabel = id === stepId
                                                        ? String(nextLabel || '').trim()
                                                        : String(existingItem?.label || '').trim();
                                                      return existingLabel ? { step_id: id, label: existingLabel } : { step_id: id };
                                                    });
                                                    return {
                                                      ...existingSignalConfig,
                                                      previous_answers: {
                                                        ...existingPrev,
                                                        mode,
                                                        source_step_ids: existingIds,
                                                        items: nextItems,
                                                      },
                                                    };
                                                  });
                                                }}
                                                placeholder="Optional label override"
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {hasInvalidSource && (
                                      <p className="text-xs text-amber-700">
                                        Some selected source steps are not valid for this step and should be removed before saving.
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        {normalizeSignalType(selectedStep.signal_config?.type || selectedStep.signal_config?.signal_type) === 'mcq' && (
                          <div className="space-y-2">
                            {(selectedStep.signal_config?.options || []).map((option: any, idx: number) => (
                              <div key={`option-${idx}`} className="flex items-center gap-2">
                                <input
                                  className="flex-1 border rounded px-3 py-2 text-sm"
                                  value={typeof option === 'string' ? option : option?.text || ''}
                                  onChange={(e) => {
                                    const options = [...(selectedStep.signal_config?.options || [])];
                                    const currentOption = options[idx];
                                    if (typeof currentOption === 'string') {
                                      options[idx] = e.target.value;
                                    } else {
                                      options[idx] = { ...currentOption, text: e.target.value };
                                    }
                                    const nextConfig = { ...selectedStep.signal_config, options } as Record<string, any>;
                                    if (typeof nextConfig.correct_index === 'number' && nextConfig.correct_index >= options.length) {
                                      const { correct_index, ...rest } = nextConfig;
                                      updateStep(selectedStep.id, { signal_config: rest });
                                      return;
                                    }
                                    updateStep(selectedStep.id, { signal_config: nextConfig });
                                  }}
                                />
                                <button
                                  className="text-gray-400 hover:text-red-500"
                                  onClick={() => {
                                    const options = [...(selectedStep.signal_config?.options || [])].filter((_: any, i: number) => i !== idx);
                                    const nextConfig = { ...selectedStep.signal_config, options } as Record<string, any>;
                                    if (typeof nextConfig.correct_index === 'number' && nextConfig.correct_index >= options.length) {
                                      const { correct_index, ...rest } = nextConfig;
                                      updateStep(selectedStep.id, { signal_config: rest });
                                      return;
                                    }
                                    updateStep(selectedStep.id, { signal_config: nextConfig });
                                  }}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              className="px-3 py-2 border rounded text-sm"
                              onClick={() => {
                                const existingOptions = selectedStep.signal_config?.options || [];
                                const isObjectFormat = existingOptions.length > 0 && typeof existingOptions[0] === 'object';
                                const newOption = isObjectFormat
                                  ? { text: `Option ${existingOptions.length + 1}`, correct: false }
                                  : `Option ${existingOptions.length + 1}`;
                                const options = [...existingOptions, newOption];
                                const nextConfig = { ...selectedStep.signal_config, options } as Record<string, any>;
                                if (typeof nextConfig.correct_index === 'number' && nextConfig.correct_index >= options.length) {
                                  const { correct_index, ...rest } = nextConfig;
                                  updateStep(selectedStep.id, { signal_config: rest });
                                  return;
                                }
                                updateStep(selectedStep.id, { signal_config: nextConfig });
                              }}
                            >
                              Add option
                            </button>
                            <div className="space-y-1">
                              <label className="text-xs uppercase tracking-wide text-gray-500">Correct answer</label>
                              <select
                                className="w-full border rounded px-3 py-2 text-sm"
                                value={
                                  typeof selectedStep.signal_config?.correct_index === 'number'
                                    ? selectedStep.signal_config.correct_index
                                    : ''
                                }
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    const { correct_index, ...rest } = selectedStep.signal_config || {};
                                    updateStep(selectedStep.id, { signal_config: rest });
                                    return;
                                  }
                                  updateStep(selectedStep.id, {
                                    signal_config: {
                                      ...selectedStep.signal_config,
                                      correct_index: Number(value),
                                    },
                                  });
                                }}
                              >
                                <option value="">Select correct answer</option>
                                {(selectedStep.signal_config?.options || []).map((option: any, idx: number) => (
                                  <option key={`correct-${idx}`} value={idx}>
                                    {typeof option === 'string' ? (option || `Option ${idx + 1}`) : (option?.text || `Option ${idx + 1}`)}
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-gray-500">
                                Enables high-confidence wrong / low-confidence right clusters during live runs.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </Tab.Panel>
                  )}
                  <Tab.Panel>
                    <button
                      className="px-3 py-2 border rounded text-sm flex items-center gap-2"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <EyeIcon className="h-4 w-4" /> Open Preview
                    </button>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
              )}
            </div>
          )}
          </div>
        </section>
        </div>
      </main>

      <StepPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} step={selectedStep as LessonStep} />

      <Dialog
        open={importModalOpen}
        onClose={() => {
          if (!importLoading) {
            resetImportModal();
            setImportModalOpen(false);
          }
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="p-5 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Import Into This Plan</Dialog.Title>
              <p className="text-sm text-gray-500 mt-1">
                {importMode === 'update'
                  ? 'Patch matching step ids and append any new steps.'
                  : 'Append imported steps after the current plan.'}
              </p>
            </div>

            <div className="p-5 space-y-4">
              {importError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {importError}
                </div>
              )}
              {importWarnings.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded space-y-2">
                  <p className="font-medium">Imported with warnings</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {importWarnings.map((warning, idx) => (
                      <li key={`${idx}-${warning}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Import mode</label>
                <div className="mt-2 inline-flex rounded border border-gray-300 bg-white p-1">
                  {(['append', 'update'] as ImportMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`px-3 py-1.5 text-sm rounded ${importMode === mode ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => {
                        setImportMode(mode);
                        clearImportPreview();
                        setImportError('');
                      }}
                      disabled={importLoading || importPreviewLoading}
                    >
                      {mode === 'append' ? 'Append' : 'Update'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">JSON file</label>
                <input
                  className="mt-2 block w-full text-sm"
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => {
                    setImportFile(e.target.files?.[0] ?? null);
                    setImportText('');
                    setImportError('');
                    clearImportPreview();
                  }}
                  disabled={importLoading || importPreviewLoading}
                />
              </div>

              <div className="text-sm text-gray-500">or paste bundle JSON</div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Bundle JSON</label>
                <textarea
                  className="mt-2 w-full border rounded px-3 py-2 text-sm font-mono h-48"
                  value={importText}
                  onChange={(e) => {
                    setImportText(e.target.value);
                    setImportFile(null);
                    setImportError('');
                    clearImportPreview();
                  }}
                  placeholder='{"bundle_type":"LessonPlanImportBundle","bundle_version":"v2","steps":[...]}'
                  disabled={importLoading || importPreviewLoading}
                />
              </div>

              {importPreview && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-indigo-900">Import Preview</p>
                    <p className="text-xs text-indigo-700 mt-1">
                      Source: {importPreview.source === 'file' ? 'File upload' : 'Pasted JSON'} • Mode: {importPreview.mode === 'update' ? 'Update' : 'Append'} • Existing: {importPreview.existingCount} • Incoming: {importPreview.incomingCount} • Updates: {importPreview.updatedCount} • Adds: {importPreview.createdCount} • Final: {importPreview.finalCount}
                    </p>
                  </div>
                  {importPreview.warnings.length > 0 && (
                    <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                      <ul className="list-disc pl-4 space-y-1">
                        {importPreview.warnings.map((warning, idx) => (
                          <li key={`preview-warning-${idx}`}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="rounded border border-indigo-100 bg-white p-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Current plan</p>
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {importPreview.existingRows.length === 0 && (
                          <p className="text-xs text-gray-500">No existing steps.</p>
                        )}
                        {importPreview.existingRows.map((row, idx) => (
                          <div key={`existing-preview-${idx}`} className="text-xs text-gray-700 flex items-center justify-between gap-2">
                            <span className="font-mono text-gray-500">{row.order}</span>
                            <span className="flex-1 truncate">{row.title}</span>
                            <span className="uppercase text-[10px] text-gray-500">{row.stageTag}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded border border-indigo-100 bg-white p-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Incoming changes</p>
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {importPreview.incomingRows.length === 0 && (
                          <p className="text-xs text-gray-500">No incoming steps.</p>
                        )}
                        {importPreview.incomingRows.map((row, idx) => (
                          <div key={`incoming-preview-${idx}`} className="text-xs text-gray-700">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-gray-500">{row.action === 'update' ? `#${row.targetStepId}` : `${row.requestedOrder} → ${row.finalOrder}`}</span>
                              <span className="uppercase text-[10px] text-gray-500">{row.stageTag}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase ${row.action === 'update' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                                {row.action === 'update' ? 'Update' : 'Add'}
                              </span>
                              <span className="truncate">{row.title}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-sm"
                  onClick={() => {
                    resetImportModal();
                    setImportModalOpen(false);
                  }}
                  disabled={importLoading || importPreviewLoading}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-sm"
                  onClick={handlePreviewImport}
                  disabled={importLoading || importPreviewLoading}
                >
                  {importPreviewLoading ? 'Previewing...' : 'Preview Import'}
                </button>
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded text-sm"
                  onClick={handleImportSteps}
                  disabled={importLoading || importPreviewLoading || !importPreview}
                >
                  {importLoading ? 'Importing...' : 'Apply Import'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-6">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="p-6 space-y-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Delete step?</Dialog.Title>
              <p className="text-sm text-gray-600">
                Delete step “{selectedStep?.title || 'Untitled step'}”?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded border border-gray-200 text-gray-700"
                  onClick={() => setConfirmDeleteOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded bg-red-600 text-white"
                  disabled={isDeletingStep}
                  onClick={async () => {
                    await deleteStep();
                    setConfirmDeleteOpen(false);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );

  if (controlledDeliveryContent) {
    if (!redesignEnabled) return controlledDeliveryContent;
    return (
      <AppShell
        role="teacher"
        navItems={teacherV2NavItems('assessments')}
        title="Assessment Delivery"
        className="max-w-none px-0 py-0"
      >
        {controlledDeliveryContent}
      </AppShell>
    );
  }

  if (!redesignEnabled) return editorContent;

  return (
    <AppShell
      role="teacher"
      navItems={teacherV2NavItems('plan')}
      title="Lesson Plan Editor"
      className="max-w-none px-0 py-0"
    >
      {editorContent}
    </AppShell>
  );
};
